/*
 * /contour-maps — контурные карты онлайн: заполнить на телефоне или компьютере
 * и распечатать на A4.
 *
 * Основа — физические слои Natural Earth (public/data/contour-maps), политика —
 * страны и субъекты из данных /true-size-map (границы по домену). Движок —
 * engine.js (проекции, полюс недоступности, подписи без наложений) и layers.js
 * (слои, поиск, подпись вдоль реки). Правила оформления — как требуют учителя
 * (docs/contour-maps.md): названия рек вдоль русла, морей и равнин — внутри,
 * хребтов — вдоль оси, вершин и городов — справа от точки; не влезает — номер
 * на карте и расшифровка в легенде; вода — синим, рельеф и города — чёрным,
 * тёплые течения — красными стрелками, холодные — синими.
 */
import * as E from './engine.js';
import * as Ly from './layers.js';
import { rasterizeSvg } from './raster.js';
import * as Pr from './practice.js';

const SLUG = 'contour-maps';
const W = window.__CONTOUR || {};
const I = W.i18n || {};
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const tr = (k, rep = {}) => String(I[k] != null ? I[k] : k).replace(/:([a-z_]+)/g, (m, n) => (rep[n] != null ? rep[n] : m));
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const LANG = W.locale || 'en';
const LOC = (window.__intlLocale && window.__intlLocale()) || 'en';
const d3 = window.d3;
const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
function ae(action, extra) { try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, action }, extra || {})); } catch (e) { /* optional */ } }
// The funnel (tool_stage; the last stage rides on tool_abandon): ready → editing → export → complete.
function stage(name, meta) { try { window.__stage && window.__stage(name, meta || {}); } catch (e) { /* optional */ } }
function diag(issue, extra) { try { window.__aeDiagnostic && window.__aeDiagnostic(Object.assign({ issue }, extra || {})); } catch (e) { /* optional */ } }
let editing = false;
function firstEdit(how) { if (editing) return; editing = true; stage('editing', { how }); }
function toast(m, t) { try { window.__toast && window.__toast(m, t || 'info'); } catch (e) { /* optional */ } }

// ── base maps ────────────────────────────────────────────────────────────
/* Россия — нормальная равнопромежуточная коническая проекция с главными
   параллелями 47° и 62°, средний меридиан 100° в. д.: так её рисуют школьные
   атласы. Полушария — две азимутальные равновеликие по меридианам 20° з. д. и
   160° в. д. Материки — азимутальная равновеликая с центром в материке. */
const BASES = {
    russia: { focus: 'RUS', panels: [{ proj: 'conicEquidistant', rotate: [-100, 0], parallels: [47, 62], fit: 'country:RUS' }], ranks: { rivers: 6, lakes: 4, marine: 6, regions: 6, points: 6, cities: 9 }, political: 'subjects', grat: 10, paper: 'land' },
    world: { panels: [{ proj: 'naturalEarth', rotate: [-10, 0], fit: 'sphere' }], ranks: { rivers: 3, lakes: 2, marine: 3, regions: 3, points: 3, cities: 3 }, political: 'countries', grat: 30, paper: 'land' },
    hemi: { panels: [{ proj: 'azimuthalEqualArea', rotate: [110, 0], fit: 'sphere', clip: 90 }, { proj: 'azimuthalEqualArea', rotate: [-70, 0], fit: 'sphere', clip: 90 }], ranks: { rivers: 3, lakes: 2, marine: 3, regions: 3, points: 3, cities: 2 }, political: 'countries', grat: 30, paper: 'land' },
    eurasia: { panels: [{ proj: 'azimuthalEqualArea', rotate: [-85, -45], box: [-12, -2, 180, 80] }], ranks: { rivers: 4, lakes: 3, marine: 4, regions: 4, points: 4, cities: 4 }, political: 'countries', grat: 20, paper: 'land' },
    europe: { panels: [{ proj: 'azimuthalEqualArea', rotate: [-15, -52], box: [-25, 34, 45, 71] }], ranks: { rivers: 5, lakes: 4, marine: 6, regions: 6, points: 6, cities: 6 }, political: 'countries', grat: 10, paper: 'land' },
    // The map of a Russian history lesson: Rus, the Horde, Muscovy — from the Baltic to the Urals.
    east: { panels: [{ proj: 'azimuthalEqualArea', rotate: [-42, -55], box: [14, 38, 70, 70] }], ranks: { rivers: 5, lakes: 4, marine: 6, regions: 6, points: 6, cities: 6 }, political: 'countries', grat: 10, paper: 'land' },
    africa: { panels: [{ proj: 'azimuthalEqualArea', rotate: [-18, -2], box: [-19, -36, 52, 38] }], ranks: { rivers: 5, lakes: 4, marine: 6, regions: 5, points: 5, cities: 5 }, political: 'countries', grat: 10, paper: 'port' },
    namerica: { panels: [{ proj: 'azimuthalEqualArea', rotate: [100, -45], box: [-168, 7, -52, 80] }], ranks: { rivers: 5, lakes: 4, marine: 6, regions: 5, points: 5, cities: 5 }, political: 'countries', grat: 10, paper: 'land' },
    samerica: { panels: [{ proj: 'azimuthalEqualArea', rotate: [60, 20], box: [-83, -56, -34, 13] }], ranks: { rivers: 5, lakes: 4, marine: 6, regions: 5, points: 5, cities: 5 }, political: 'countries', grat: 10, paper: 'port' },
    australia: { panels: [{ proj: 'azimuthalEqualArea', rotate: [-150, 25], box: [110, -48, 180, -5] }], ranks: { rivers: 6, lakes: 5, marine: 6, regions: 6, points: 6, cities: 6 }, political: 'countries', grat: 10, paper: 'land' },
    antarctica: { panels: [{ proj: 'azimuthalEqualArea', rotate: [0, 90], box: [-180, -90, 180, -58] }], ranks: { rivers: 0, lakes: 0, marine: 6, regions: 6, points: 6, cities: 0 }, political: null, grat: 30, paper: 'port' },
};
const BASE_ORDER = ['russia', 'world', 'hemi', 'eurasia', 'europe', 'east', 'africa', 'namerica', 'samerica', 'australia', 'antarctica'];

/* Знаки школьного атласа. У разных атласов значки полезных ископаемых
   различаются, поэтому подпись каждого знака в легенде меняется. */
const SYMBOLS = {
    city: { draw: (x, y, c) => `<circle cx="${x}" cy="${y}" r="3.2" fill="${c || '#111827'}"/>` },
    capital: { draw: (x, y, c) => `<circle cx="${x}" cy="${y}" r="5" fill="#fff" stroke="${c || '#111827'}" stroke-width="1.6"/><circle cx="${x}" cy="${y}" r="2.2" fill="${c || '#111827'}"/>` },
    peak: { draw: (x, y, c) => `<path d="M${x} ${y - 5}L${x + 4.5} ${y + 3.5}H${x - 4.5}Z" fill="${c || '#111827'}"/>` },
    volcano: { draw: (x, y) => `<path d="M${x} ${y - 5}L${x + 4.5} ${y + 3.5}H${x - 4.5}Z" fill="#dc2626"/><path d="M${x - 2} ${y - 7}l2-3 2 3" fill="none" stroke="#dc2626" stroke-width="1.2"/>` },
    oil: { draw: (x, y) => `<path d="M${x} ${y - 5}C${x + 4} ${y} ${x + 4} ${y + 4} ${x} ${y + 4}C${x - 4} ${y + 4} ${x - 4} ${y} ${x} ${y - 5}Z" fill="#111827"/>` },
    gas: { draw: (x, y) => `<path d="M${x} ${y - 5}C${x + 4} ${y} ${x + 4} ${y + 4} ${x} ${y + 4}C${x - 4} ${y + 4} ${x - 4} ${y} ${x} ${y - 5}Z" fill="#2563eb"/>` },
    coal: { draw: (x, y) => `<rect x="${x - 4}" y="${y - 4}" width="8" height="8" fill="#111827"/>` },
    iron: { draw: (x, y) => `<path d="M${x} ${y - 5}L${x + 5} ${y + 4}H${x - 5}Z" fill="#dc2626"/>` },
    gold: { draw: (x, y) => `<circle cx="${x}" cy="${y}" r="4.2" fill="#eab308" stroke="#854d0e" stroke-width="1"/>` },
    diamond: { draw: (x, y) => `<path d="M${x} ${y - 5}L${x + 4.5} ${y}L${x} ${y + 5}L${x - 4.5} ${y}Z" fill="none" stroke="#111827" stroke-width="1.6"/>` },
    plant: { draw: (x, y) => `<rect x="${x - 4.5}" y="${y - 4.5}" width="9" height="9" fill="#fff" stroke="#111827" stroke-width="1.5"/><path d="M${x - 2.5} ${y + 2.5}V${y - 1}l2.5 2V${y - 1}l2.5 2v1.5" fill="none" stroke="#111827" stroke-width="1"/>` },
    port: { draw: (x, y) => `<circle cx="${x}" cy="${y - 2}" r="1.6" fill="none" stroke="#1d4ed8" stroke-width="1.2"/><path d="M${x} ${y - 0.4}V${y + 4.5}M${x - 3.5} ${y + 2}c1 2 6 2 7 0" fill="none" stroke="#1d4ed8" stroke-width="1.2"/>` },
    battle: { draw: (x, y) => `<path d="M${x - 4.5} ${y - 4.5}L${x + 4.5} ${y + 4.5}M${x + 4.5} ${y - 4.5}L${x - 4.5} ${y + 4.5}" stroke="#b91c1c" stroke-width="2" stroke-linecap="round"/>` },
    lake: { draw: (x, y) => `<ellipse cx="${x}" cy="${y}" rx="4.2" ry="3" fill="#bfdbfe" stroke="#1d4ed8" stroke-width="1"/>` },
    star: { draw: (x, y, c) => `<path d="M${x} ${y - 5.5}l1.6 3.5 3.8.4-2.9 2.5.9 3.7-3.4-2-3.4 2 .9-3.7-2.9-2.5 3.8-.4z" fill="${c || '#dc2626'}"/>` },
};
const ARROWS = { warm: '#dc2626', cold: '#2563eb', route: '#111827', campaign: '#b91c1c', border: '#dc2626' };
/* Природные зоны «как в атласе» (zones.json, RESOLVE Ecoregions 2017): цвета школьного атласа, светлые
   настолько, чтобы чёрная подпись читалась поверх. В ч/б — оттенки серого. */
const ZONE_COLORS = {
    ice: '#eef4fa', tundra: '#d9cdea', taiga: '#8fbf86', mixed: '#b7dc9a', forest_steppe: '#dfe6a0', steppe: '#f3e3a0',
    desert: '#f7d4a8', medit: '#efc0d6', monsoon: '#a6d9b0', savanna: '#ead48a', equat: '#5fae72', mountain: '#cdbfae',
};
const ZONE_GRAYS = {
    ice: '#ffffff', tundra: '#ededed', taiga: '#9a9a9a', mixed: '#bdbdbd', forest_steppe: '#d0d0d0', steppe: '#e0e0e0',
    desert: '#f5f5f5', medit: '#c8c8c8', monsoon: '#b0b0b0', savanna: '#dadada', equat: '#8a8a8a', mountain: '#c2c2c2',
};
const PALETTE = ['#93c5fd', '#86efac', '#fde68a', '#fdba74', '#d6a77a', '#fca5a5', '#c4b5fd', '#f9a8d4', '#d1d5db', '#a3e635'];
const GRAYS = { '#93c5fd': '#e5e7eb', '#86efac': '#d1d5db', '#fde68a': '#f3f4f6', '#fdba74': '#9ca3af', '#d6a77a': '#6b7280', '#fca5a5': '#cbd5e1', '#c4b5fd': '#a1a1aa', '#f9a8d4': '#e4e4e7', '#d1d5db': '#d4d4d8', '#a3e635': '#bdbdbd' };

// ── state ────────────────────────────────────────────────────────────────
function freshState(base) {
    const b = base || W.defaultBase || 'russia';
    return {
        v: 2, base: b, political: false,
        layers: { rivers: true, lakes: true, grat: true, glines: true, coast: true, zones: '', hist: '' },
        bw: false, header: true, title: tr('title_' + b), student: '', numbered: false,
        items: [], legend: {}, nextNum: 1,
    };
}
let S = freshState();
let tool = 'select', symbol = 'city', arrowStyle = 'warm', fillColor = PALETTE[0], textSize = 13;
let drafts = null;
const hist = { stack: [], pos: -1, lock: false };
const VB_W = 1000;

// ── data ─────────────────────────────────────────────────────────────────
const cache = {};
async function getJson(url) {
    const r = await fetch(url, { credentials: 'omit' });
    if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + url);
    return r.json();
}
async function layer(name) {
    if (cache[name]) return cache[name];
    if (name === 'countries') cache[name] = E.fixWinding(E.decode(await getJson(W.worldUrl)), d3).map((f) => Object.assign(f, { kind: 'country', rank: 0 }));
    else if (name === 'subjects') cache[name] = E.fixWinding(E.decode(await getJson(W.subjectsUrl)), d3).map((f) => Object.assign(f, { kind: 'subject', rank: 0 }));
    else {
        const [own, extra] = await Promise.all([getJson(W.layerUrl.replace('__L__', name)), extraLayer()]);
        cache[name] = Ly.mergeExtra(name, Ly.decodeLayer(own, d3), extra);
    }
    return cache[name];
}
/* School nomenclature Natural Earth lacks (continents, the Apennine Peninsula, the uplands of the
   Russian Plain, capes at the ends of the continents, rivers of Russia from OpenStreetMap). A map
   without it is still a map: a failed load leaves the Natural Earth layers as they are. */
let extraP = null;
/* Границы государств года из школьной истории (hist-<год>.json, Cliopatria): по запросу, 40–200 КБ.
   У объекта — имя своего слоя: заливка и подпись найдут его после перезагрузки страницы. */
const histP = new Map();
function histLayer(year) {
    if (!year) return Promise.resolve([]);
    if (!histP.has(year)) {
        histP.set(year, layer('hist-' + year).then((fs) => fs.map((f) => Object.assign(f, { layerName: 'hist-' + year })))
            .catch((e) => { histP.delete(year); diag('hist_failed', { year, msg: String((e && e.message) || e).slice(0, 120) }); toast(tr('hist_failed'), 'error'); return []; }));
    }
    return histP.get(year);
}
/* Природные зоны — по запросу (≈ 0,5 МБ): страница без них их не грузит. */
let zonesP = null;
function zonesLayer() {
    if (!zonesP) {
        zonesP = layer('zones').then((fs) => fs.map((f) => Object.assign(f, { zone: String(f.id).replace(/^zone-/, '') })))
            .catch((e) => { zonesP = null; diag('zones_failed', { msg: String((e && e.message) || e).slice(0, 120) }); toast(tr('zones_failed'), 'error'); return []; });
    }
    return zonesP;
}
// Objects whose lines come from OpenStreetMap: a map that shows one names its source (ODbL).
const OSM_IDS = new Set();
function extraLayer() {
    if (!extraP) {
        extraP = getJson(W.layerUrl.replace('__L__', 'extra'))
            .then((j) => {
                const feats = Ly.decodeLayer(j, d3);
                for (const f of feats) if (f.osm) OSM_IDS.add(String(f.id));
                return { fix: j.fix || [], feats };
            })
            .catch((e) => { diag('extra_layer_failed', { msg: String((e && e.message) || e).slice(0, 120) }); return null; });
    }
    return extraP;
}
let index = null;
async function ensureIndex() {
    if (index) return index;
    const names = ['rivers', 'lakes', 'marine', 'regions', 'points', 'cities', 'lines', 'countries'];
    if (S.base === 'russia') names.push('subjects');
    const all = (await Promise.all(names.map(layer))).flat().concat(await histLayer(S.layers.hist));
    index = Ly.buildIndex(all, LANG, Object.assign({}, W.featureAliases || {}, W.aliases || {}));
    return index;
}

// ── geometry of the base ─────────────────────────────────────────────────
let G = null;   // {key, panels:[{p, rect, path}], H, top}
function boxTarget([x0, y0, x1, y1]) {
    const pts = [];
    for (let i = 0; i <= 24; i++) { pts.push([x0 + (x1 - x0) * i / 24, y0]); pts.push([x0 + (x1 - x0) * i / 24, y1]); }
    for (let i = 0; i <= 24; i++) { pts.push([x0, y0 + (y1 - y0) * i / 24]); pts.push([x1, y0 + (y1 - y0) * i / 24]); }
    return { type: 'MultiPoint', coordinates: pts };
}
async function geometry() {
    const b = BASES[S.base];
    const key = S.base;
    if (G && G.key === key) return G;
    const n = b.panels.length, gap = n > 1 ? 16 : 0;
    const pw = (VB_W - 16 - gap * (n - 1)) / n;
    const panels = [];
    let H = 0;
    for (let i = 0; i < n; i++) {
        const sp = b.panels[i];
        const p = E.makeProjection({ id: sp.proj, rotate: sp.rotate, parallels: sp.parallels }, d3);
        if (sp.clip && p.clipAngle) p.clipAngle(sp.clip);
        let target;
        if (sp.fit === 'sphere') target = { type: 'Sphere' };
        else if (sp.fit && sp.fit.startsWith('country:')) target = (await layer('countries')).find((f) => f.id === sp.fit.slice(8));
        else target = boxTarget(sp.box);
        p.fitWidth(pw, target);
        const path = d3.geoPath(p);
        const bb = path.bounds(target);
        const h = Math.min(1250, Math.ceil(bb[1][1] - bb[0][1]));
        const x0 = 8 + i * (pw + gap);
        p.translate([p.translate()[0] + x0 - bb[0][0], p.translate()[1] - bb[0][1]]);
        if (sp.fit !== 'sphere' && p.clipExtent) p.clipExtent([[x0, 0], [x0 + pw, h]]);
        panels.push({ p, rect: [x0, 0, x0 + pw, h], path: d3.geoPath(p), sphere: sp.fit === 'sphere' });
        H = Math.max(H, h);
    }
    G = { key, panels, H };
    return G;
}

// ── render ───────────────────────────────────────────────────────────────
let baseSvg = { key: '', html: '' };
async function buildBase() {
    const b = BASES[S.base];
    const key = [S.base, S.political, JSON.stringify(S.layers), S.bw, zonesP ? 1 : 0, histP.size].join('|');
    if (baseSvg.key === key) return baseSvg.html;
    const g = await geometry();
    const [land, rivers, lakes, glines] = await Promise.all([layer('land'), b.ranks.rivers ? layer('rivers') : [], b.ranks.lakes ? layer('lakes') : [], layer('lines')]);
    const zones = S.layers.zones ? await zonesLayer() : [];
    const hist = S.layers.hist ? await histLayer(S.layers.hist) : [];
    const pol = S.political && b.political ? await layer(b.political) : [];
    const countries = b.focus ? await layer('countries') : [];
    const focus = b.focus ? countries.find((f) => f.id === b.focus) : null;
    const water = S.bw ? '#ffffff' : '#e0f2fe', waterLine = S.bw ? '#111827' : '#1d4ed8';
    const out = [];
    out.push(`<defs><clipPath id="cm-clip"><rect x="0" y="0" width="${VB_W}" height="${g.H}"/></clipPath></defs>`);
    for (const pn of g.panels) {
        const P0 = pn.path;
        // An empty layer (no lakes on the Antarctica page) gives null, not an empty path.
        const P = (o) => P0(o) || 'M0 0';
        if (pn.sphere) out.push(`<path d="${P({ type: 'Sphere' })}" fill="${water}" stroke="#111827" stroke-width="1.2"/>`);
        else out.push(`<rect x="${pn.rect[0]}" y="0" width="${pn.rect[2] - pn.rect[0]}" height="${pn.rect[3]}" fill="${water}" stroke="#111827" stroke-width="1.2"/>`);
        out.push(`<g class="cm-land"><path d="${P({ type: 'GeometryCollection', geometries: land.map((f) => f.geometry) })}" fill="#ffffff" stroke="${S.layers.coast ? '#111827' : 'none'}" stroke-width=".8" stroke-linejoin="round"/></g>`);
        if (S.layers.zones && zones.length) {
            // «color» — the atlas page; «lines» — the zones' borders only, for a pupil to colour in.
            for (const z of zones) {
                const d = P0(z);
                if (!d) continue;
                if (S.layers.zones === 'color') out.push(`<path d="${d}" fill="${S.bw ? ZONE_GRAYS[z.zone] : ZONE_COLORS[z.zone]}" stroke="#64748b" stroke-width=".35" stroke-opacity=".7"/>`);
                else out.push(`<path d="${d}" fill="none" stroke="#475569" stroke-width=".55" stroke-dasharray="3 2"/>`);
            }
        }
        if (focus && !hist.length) {
            // Like an atlas page of one country: the neighbours grey, the country white with its border drawn.
            const others = countries.filter((f) => f.id !== focus.id);
            out.push(`<path d="${P({ type: 'GeometryCollection', geometries: others.map((f) => f.geometry) })}" fill="${S.bw ? '#f3f4f6' : '#eef0f3'}" stroke="none"/>`);
        }
        // User fills sit under water and lines, like pencil under ink.
        out.push(`<g class="cm-fills" data-panel="${g.panels.indexOf(pn)}"></g>`);
        if (S.layers.lakes) {
            const lk = lakes.filter((f) => f.rank <= b.ranks.lakes);
            out.push(`<path d="${P({ type: 'GeometryCollection', geometries: lk.map((f) => f.geometry) })}" fill="${water}" stroke="${waterLine}" stroke-width=".6"/>`);
        }
        if (S.layers.rivers) {
            const rv = rivers.filter((f) => f.rank <= b.ranks.rivers && f.kind === 'river');
            out.push(`<path d="${P({ type: 'GeometryCollection', geometries: rv.map((f) => f.geometry) })}" fill="none" stroke="${waterLine}" stroke-width=".7" stroke-linecap="round" stroke-linejoin="round"/>`);
        }
        if (pol.length) out.push(`<path d="${P({ type: 'GeometryCollection', geometries: pol.map((f) => f.geometry) })}" fill="none" stroke="#6b7280" stroke-width=".5" stroke-linejoin="round"/>`);
        if (focus && !hist.length) out.push(`<path d="${P(focus)}" fill="none" stroke="${S.bw ? '#111827' : '#b91c1c'}" stroke-width="1.6" stroke-linejoin="round" stroke-opacity=".9"/>`);
        // The borders of the year, over the pupil's colours (like the modern borders).
        if (hist.length) out.push(`<path d="${P({ type: 'GeometryCollection', geometries: hist.map((f) => f.geometry) })}" fill="none" stroke="${S.bw ? '#111827' : '#7c2d12'}" stroke-width=".9" stroke-linejoin="round"/>`);
        if (S.layers.grat) out.push(`<path d="${P(d3.geoGraticule().step([b.grat, b.grat])())}" fill="none" stroke="#64748b" stroke-opacity=".55" stroke-width=".45"/>`);
        if (S.layers.glines) {
            const gl = glines.filter((f) => /Equator|Tropic|Arctic|Antarctic/i.test(f.names.en || ''));
            out.push(`<path d="${P({ type: 'GeometryCollection', geometries: gl.map((f) => f.geometry) })}" fill="none" stroke="#111827" stroke-width=".7" stroke-dasharray="5 4"/>`);
        }
        if (S.layers.grat) out.push(gratLabels(pn, b.grat));
    }
    baseSvg = { key, html: out.join('') };
    return baseSvg.html;
}
/* Подписи градусов у рамки: меридиан — там, где он уходит за нижний край
   (или верхний), параллель — у левого края. Без них не выполнить задание
   «нанесите точку по координатам». */
function gratLabels(pn, step) {
    const p = pn.p, [x0, , x1, h] = pn.rect, out = [];
    const fmt = (v, pos, neg) => `${Math.abs(v)}°${v === 0 || Math.abs(v) === 180 ? '' : (v > 0 ? pos : neg)}`;
    for (let lon = -180; lon < 180; lon += step) {
        let best = null;
        for (let lat = -88; lat <= 88; lat += 1) {
            const q = p([lon, lat]);
            if (!q || !isFinite(q[0]) || q[0] < x0 + 4 || q[0] > x1 - 4 || q[1] < 4 || q[1] > h - 4) continue;
            if (pn.sphere) { if (Math.abs(lat) < 1e-9) best = q; }
            else if (!best || q[1] > best[1]) best = q;
        }
        if (best) out.push(`<text x="${best[0].toFixed(1)}" y="${(Math.min(h - 3, best[1] + (pn.sphere ? 11 : -3))).toFixed(1)}" text-anchor="middle" font-family='${FONT}' font-size="9" fill="#334155">${esc(fmt(lon, tr('deg_e'), tr('deg_w')))}</text>`);
    }
    for (let lat = -80; lat <= 80; lat += step) {
        let best = null;
        for (let lon = -180; lon <= 180; lon += 1) {
            const q = p([lon, lat]);
            if (!q || !isFinite(q[0]) || q[0] < x0 + 2 || q[0] > x1 - 4 || q[1] < 6 || q[1] > h - 6) continue;
            if (!best || q[0] < best[0]) best = q;
        }
        if (best) out.push(`<text x="${(best[0] + 3).toFixed(1)}" y="${(best[1] - 2).toFixed(1)}" font-family='${FONT}' font-size="9" fill="#334155">${esc(fmt(lat, tr('deg_n'), tr('deg_s')))}</text>`);
    }
    return out.join('');
}

const STYLE = {
    water: { fill: '#1d4ed8', italic: true }, relief: { fill: '#111827' }, place: { fill: '#111827' }, user: { fill: '#111827' },
};
function kindStyle(kind) {
    if (['river', 'river_int', 'lake', 'reservoir', 'ocean', 'sea', 'bay', 'gulf', 'strait', 'lagoon', 'lake_pt'].includes(kind)) return STYLE.water;
    if (['capital', 'city1', 'city', 'country', 'subject'].includes(kind)) return STYLE.place;
    return STYLE.relief;
}
function labelSvg(it, selected) {
    const st = it.style === 'water' && !S.bw ? STYLE.water : (it.style === 'water' ? { fill: '#111827', italic: true } : STYLE.relief);
    const color = it.color || st.fill;
    const common = `font-family='${FONT}' font-size="${it.size}" font-weight="${it.bold ? 700 : 500}"${st.italic ? ' font-style="italic"' : ''} fill="${S.bw ? '#111827' : color}"${it.ls ? ` letter-spacing="${it.ls}"` : ''} stroke="#ffffff" stroke-width="2.4" stroke-opacity=".85" paint-order="stroke"`;
    const sel = selected ? ' class="cm-sel"' : '';
    if (it.path) {
        return `<g data-item="${it.id}"${sel}><path id="cmp-${it.id}" d="${it.path}" fill="none" stroke="none"/><text ${common}><textPath href="#cmp-${it.id}" startOffset="50%" text-anchor="middle">${esc(it.text)}</textPath></text></g>`;
    }
    const rot = it.rot ? ` transform="rotate(${it.rot.toFixed(1)} ${it.x.toFixed(1)} ${it.y.toFixed(1)})"` : '';
    return `<g data-item="${it.id}"${sel}><text x="${it.x.toFixed(1)}" y="${it.y.toFixed(1)}" text-anchor="${it.anchor || 'middle'}" ${common}${rot}>${esc(it.text)}</text></g>`;
}
function itemSvg(it, selected) {
    const sel = selected ? ' class="cm-sel"' : '';
    // Not selectable on their own: they came with a label (same ref) and go with it.
    if (it.t === 'line') {
        if (it.style === 'boundary') return `<path d="${it.d}" fill="none" stroke="${S.bw ? '#111827' : '#7e22ce'}" stroke-width="1.6" stroke-dasharray="7 4" stroke-linejoin="round"/>`;
        if (it.style === 'current_w' || it.style === 'current_c') {
            const c = S.bw ? '#111827' : ARROWS[it.style === 'current_w' ? 'warm' : 'cold'];
            const dash = it.style === 'current_c' && S.bw ? ' stroke-dasharray="5 3"' : '';
            const h = it.head;
            const head = h ? `<path d="M${h[0]} ${h[1]} L${(h[0] - 9 * Math.cos(h[2] - 0.45)).toFixed(1)} ${(h[1] - 9 * Math.sin(h[2] - 0.45)).toFixed(1)} L${(h[0] - 9 * Math.cos(h[2] + 0.45)).toFixed(1)} ${(h[1] - 9 * Math.sin(h[2] + 0.45)).toFixed(1)}Z" fill="${c}"/>` : '';
            return `<g><path d="${it.d}" fill="none" stroke="${c}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"${dash}/>${head}</g>`;
        }
        return `<path d="${it.d}" fill="none" stroke="${S.bw ? '#111827' : '#1d4ed8'}" stroke-width=".8" stroke-linecap="round" stroke-linejoin="round"/>`;
    }
    if (it.t === 'shape') return `<path d="${it.d}" fill="${S.bw ? '#ffffff' : '#e0f2fe'}" stroke="${S.bw ? '#111827' : '#1d4ed8'}" stroke-width=".6"/>`;
    if (it.t === 'label') return labelSvg(it, selected);
    if (it.t === 'sym') return `<g data-item="${it.id}"${sel}>${(SYMBOLS[it.sym] || SYMBOLS.city).draw(it.x, it.y, S.bw ? '#111827' : it.color)}</g>`;
    if (it.t === 'num') return `<g data-item="${it.id}"${sel}><circle cx="${it.x}" cy="${it.y}" r="7" fill="#fff" stroke="#111827" stroke-width="1"/><text x="${it.x}" y="${it.y + 3.5}" text-anchor="middle" font-family='${FONT}' font-size="9.5" font-weight="700" fill="#111827">${it.n}</text></g>`;
    if (it.t === 'arrow') {
        const c = S.bw ? '#111827' : (ARROWS[it.style] || '#111827');
        const d = 'M' + it.pts.map((p) => p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' L');
        const n = it.pts.length, a = it.pts[n - 2], z = it.pts[n - 1];
        const ang = Math.atan2(z[1] - a[1], z[0] - a[0]);
        const head = it.style === 'border' ? '' : `<path d="M${z[0]} ${z[1]} L${(z[0] - 9 * Math.cos(ang - 0.45)).toFixed(1)} ${(z[1] - 9 * Math.sin(ang - 0.45)).toFixed(1)} L${(z[0] - 9 * Math.cos(ang + 0.45)).toFixed(1)} ${(z[1] - 9 * Math.sin(ang + 0.45)).toFixed(1)}Z" fill="${c}"/>`;
        const dash = it.style === 'border' ? ' stroke-dasharray="7 4"' : (it.style === 'cold' && S.bw ? ' stroke-dasharray="5 3"' : '');
        return `<g data-item="${it.id}"${sel}><path d="${d}" fill="none" stroke="${c}" stroke-width="${it.style === 'route' ? 1.6 : 2.2}" stroke-linecap="round" stroke-linejoin="round"${dash}/>${head}</g>`;
    }
    return '';
}

async function fillsSvg() {
    const fills = S.items.filter((i) => i.t === 'fill');
    if (!fills.length) return [];
    const g = await geometry();
    const out = g.panels.map(() => []);
    for (const it of fills) {
        const feats = await layer(it.layer);
        const f = feats.find((x) => String(x.id) === String(it.ref));
        if (!f) continue;
        const color = S.bw ? (GRAYS[it.color] || '#d1d5db') : it.color;
        g.panels.forEach((pn, i) => { const d = pn.path(f); if (d) out[i].push(`<path data-item="${it.id}" d="${d}" fill="${color}" fill-opacity="${S.bw ? 1 : 0.85}"/>`); });
    }
    return out;
}

let selectedId = null;
let zonesSeen = [], zonesSeenKey = '';
/* The last render wins: a base switched while the previous one's layers were still loading
   must not be painted over by the older, slower render. */
let renderSeq = 0;
async function render() {
    const seq = ++renderSeq;
    const g = await geometry();
    const base = await buildBase();
    if (seq !== renderSeq || g.key !== S.base) return;
    const header = S.header ? 64 : 0;
    // «Check yourself» asks on a blank map; a test variant shows numbers where the names stood.
    const quiz = QZ.on;
    const nums = !quiz && S.numbered ? Pr.numberedView(S.items, textW) : null;
    // Which zones the map shows — once per base: the legend lists only those (no savanna on the map of Russia).
    const zk = S.layers.zones === 'color' ? S.base : '';
    if (zk !== zonesSeenKey) {
        zonesSeen = [];
        if (zk) for (const z of await zonesLayer()) if (g.panels.some((pn) => pn.path.area(z) > 40)) zonesSeen.push(z.zone);
        if (seq !== renderSeq) return;
        zonesSeenKey = zk;
    }
    const legend = quiz ? [] : legendModel(nums);
    const legRows = layoutLegend(legend);
    const legH = legRows.length ? legRows.length * 22 + 30 : 0;
    const H = header + g.H + legH + 8;
    const svg = $('#cm-svg');
    svg.setAttribute('viewBox', `0 0 ${VB_W} ${H}`);
    const parts = [`<rect width="${VB_W}" height="${H}" fill="#ffffff"/>`];
    if (S.header) {
        parts.push(`<text x="${VB_W / 2}" y="26" text-anchor="middle" font-family='${FONT}' font-size="20" font-weight="700" fill="#111827">${esc(S.title || tr('title_' + S.base))}</text>`);
        // The name line starts where the reader starts: on the right of an Arabic page.
        const rtl = document.documentElement.getAttribute('dir') === 'rtl';
        parts.push(`<text x="${rtl ? VB_W - 16 : 16}" y="52"${rtl ? ' direction="rtl"' : ''} font-family='${FONT}' font-size="13" fill="#111827">${esc(tr('student_line', { name: S.student || '______________________________' }))}</text>`);
    }
    parts.push(`<g transform="translate(0 ${header})"><g id="cm-zoom">${base}`);
    parts.push('<g class="cm-items" clip-path="url(#cm-clip)">');
    if (!quiz) {
        for (const it of S.items) {
            if (it.t === 'fill' || (nums && (it.t === 'label' || it.t === 'num'))) continue;
            parts.push(itemSvg(it, it.id === selectedId));
        }
    }
    if (nums) for (const n of nums) parts.push(numSvg(n));
    parts.push('</g><g id="cm-flash"></g><g id="cm-qz" pointer-events="none"></g><g id="cm-draft"></g></g></g>');
    const usesOsm = quiz ? QZ.list.some((x) => OSM_IDS.has(String(x.f.id))) : S.items.some((i) => i.ref != null && OSM_IDS.has(String(i.ref)));
    // What the map's licences ask to be named on it: OpenStreetMap (ODbL) for its rivers, RESOLVE (CC BY) for the zones.
    const credits = [];
    if (OSM_IDS.size && usesOsm) credits.push(tr('credit_osm'));
    if (S.layers.zones && zonesP) credits.push(tr('credit_zones'));
    if (S.layers.hist) credits.push(tr('credit_hist', { year: S.layers.hist }));
    if (credits.length) {
        parts.push(`<text x="${VB_W - 10}" y="${header + g.H - 5}" text-anchor="end" font-family='${FONT}' font-size="8.5" fill="#475569" stroke="#ffffff" stroke-width="2" paint-order="stroke">${esc(credits.join(' · '))}</text>`);
    }
    if (legRows.length) parts.push(legendSvg(legRows, header + g.H + 10));
    svg.innerHTML = parts.join('');
    const fills = quiz ? [] : await fillsSvg();
    if (seq !== renderSeq) return;
    if (fills.length) $$('.cm-fills', svg).forEach((el) => { el.innerHTML = fills[+el.dataset.panel].join(''); });
    applyZoom();
    renderItemsPanel();
}

// ── legend ───────────────────────────────────────────────────────────────
const ANSWER_LINE = '____________________';
function numSvg(n) {
    return `<g class="cm-tn"><circle cx="${n.x}" cy="${n.y}" r="7" fill="#fff" stroke="#111827" stroke-width="1"/><text x="${n.x}" y="${n.y + 3.5}" text-anchor="middle" font-family='${FONT}' font-size="9.5" font-weight="700" fill="#111827">${n.n}</text></g>`;
}
function legendModel(nums) {
    const out = [];
    const seenSym = new Set(), seenArr = new Set(), seenFill = new Set();
    for (const it of S.items) {
        if (it.t === 'sym' && !seenSym.has(it.sym)) { seenSym.add(it.sym); out.push({ kind: 'sym', key: 'sym:' + it.sym, sym: it.sym, color: it.color, label: S.legend['sym:' + it.sym] ?? tr('sym_' + it.sym) }); }
        // A current from the data is a warm or a cold arrow in the legend, like one drawn by hand.
        const arr = it.t === 'arrow' ? it.style : (it.t === 'line' && it.style === 'current_w' ? 'warm' : it.t === 'line' && it.style === 'current_c' ? 'cold' : null);
        if (arr && !seenArr.has(arr)) { seenArr.add(arr); out.push({ kind: 'arrow', key: 'arr:' + arr, style: arr, label: S.legend['arr:' + arr] ?? tr('arr_' + arr) }); }
        if (it.t === 'fill' && !seenFill.has(it.color)) { seenFill.add(it.color); out.push({ kind: 'fill', key: 'fill:' + it.color, color: it.color, label: S.legend['fill:' + it.color] ?? '' }); }
    }
    if (S.layers.zones === 'color' && zonesSeen.length) for (const z of zonesSeen) out.push({ kind: 'fill', key: 'zone:' + z, color: S.bw ? ZONE_GRAYS[z] : ZONE_COLORS[z], label: S.legend['zone:' + z] ?? tr('zone_' + z) });
    // A test variant: under each number a line to write the answer on — the names go to the key sheet.
    if (nums) for (const n of nums) out.push({ kind: 'num', key: 'tn:' + n.n, n: n.n, label: ANSWER_LINE });
    else for (const it of S.items) if (it.t === 'num') out.push({ kind: 'num', key: 'num:' + it.id, n: it.n, label: it.text });
    return out;
}
function layoutLegend(items) {
    const rows = [];
    let row = [], x = 0;
    const measure = (t) => textW(t, 12);
    for (const it of items) {
        const w = 26 + measure(it.label || '—') + 18;
        if (x + w > VB_W - 32 && row.length) { rows.push(row); row = []; x = 0; }
        row.push(Object.assign({ x, w }, it)); x += w;
    }
    if (row.length) rows.push(row);
    return rows;
}
function legendSvg(rows, y0) {
    const out = [`<text x="16" y="${y0 + 12}" font-family='${FONT}' font-size="13" font-weight="700" fill="#111827">${esc(tr('legend'))}</text>`];
    let y = y0 + 22;
    for (const row of rows) {
        for (const it of row) {
            const x = 16 + it.x, cy = y + 7;
            if (it.kind === 'sym') out.push((SYMBOLS[it.sym] || SYMBOLS.city).draw(x + 8, cy, S.bw ? '#111827' : it.color));
            else if (it.kind === 'arrow') out.push(itemSvg({ t: 'arrow', id: 'leg', style: it.style, pts: [[x, cy], [x + 18, cy]] }).replace(/data-item="leg"/, ''));
            else if (it.kind === 'fill') out.push(`<rect x="${x}" y="${y}" width="16" height="13" fill="${S.bw ? (GRAYS[it.color] || '#d1d5db') : it.color}" stroke="#111827" stroke-width=".6"/>`);
            else if (it.kind === 'num') out.push(`<circle cx="${x + 8}" cy="${cy}" r="7" fill="#fff" stroke="#111827" stroke-width="1"/><text x="${x + 8}" y="${cy + 3.5}" text-anchor="middle" font-family='${FONT}' font-size="9.5" font-weight="700" fill="#111827">${it.n}</text>`);
            out.push(`<text x="${x + 24}" y="${cy + 4}" font-family='${FONT}' font-size="12" fill="#111827">${esc(it.label || '—')}</text>`);
        }
        y += 22;
    }
    return out.join('');
}

// ── text measure ─────────────────────────────────────────────────────────
let mctx = null;
function textW(t, size, bold) {
    if (!mctx) mctx = document.createElement('canvas').getContext('2d');
    mctx.font = `${bold ? 700 : 500} ${size}px ${FONT}`;
    return mctx.measureText(t).width;
}

// ── labelling an object by the rules ──────────────────────────────────────
let uid = Date.now() % 1e6;
const nid = () => 'i' + (++uid).toString(36);
/* Returns false when the object does not fall on this base (Hudson Bay on «Russia»); the caller
   then says where it is instead of a silent «not found». quiet — no toast of its own. */
async function labelObject(f, quiet) {
    const g = await geometry();
    const name = f.names[LANG] || f.names.en || '';
    if (!name) return;
    const style = kindStyle(f.kind) === STYLE.water ? 'water' : 'relief';
    // Pick the panel where the object is largest.
    let pn = g.panels[0], bestA = -1;
    for (const p of g.panels) { const a = p.path.area(f) + p.path.measure(f); if (a > bestA) { bestA = a; pn = p; } }
    const proj = pn.p;
    const b = BASES[S.base];
    if (Ly.LINE_KINDS.has(f.kind)) {
        const size = f.rank <= 2 ? 12 : 11;
        const lp = Ly.riverLabelPath(f, proj, textW(name, size));
        if (!lp) return false;
        // A line the base does not draw — the Europe–Asia boundary, a river below the base's rank
        // (Исеть on the map of Russia) — is drawn with its label: a name along nothing is no answer.
        if (f.kind === 'boundary' || f.kind === 'current_w' || f.kind === 'current_c' || (f.kind === 'river' && (!S.layers.rivers || f.rank > b.ranks.rivers))) {
            const d = pn.path(f);
            const style = f.kind === 'river' ? 'river' : f.kind;
            // The arrowhead of a current: the direction of its last step, the way it flows.
            let head;
            if (style.startsWith('current_')) {
                const ln = f.geometry.coordinates[0], z = proj(ln[ln.length - 1]), a = proj(ln[ln.length - 2]);
                if (z && a && inPanel(pn, z)) head = [+z[0].toFixed(1), +z[1].toFixed(1), +Math.atan2(z[1] - a[1], z[0] - a[0]).toFixed(3)];
            }
            if (d) S.items.push({ id: nid(), t: 'line', style, d: roundPath(d), head, ref: f.id });
        }
        if (!lp.short) S.items.push({ id: nid(), t: 'label', text: name, size, style, path: lp.d, ref: f.id });
        else numberObject(f, lp.at[0], lp.at[1]);
        return;
    }
    if (Ly.POINT_KINDS.has(f.kind)) {
        const q = proj(f.geometry.coordinates);
        if (!q || !inPanel(pn, q)) { if (!quiet) toast(tr('not_on_map'), 'info'); return false; }
        const sym = f.kind === 'peak' ? 'peak' : f.kind === 'capital' ? 'capital' : f.kind === 'lake_pt' ? 'lake' : ['cape', 'pole', 'spit', 'island_pt', 'waterfall'].includes(f.kind) ? null : 'city';
        if (sym) S.items.push({ id: nid(), t: 'sym', sym, x: +q[0].toFixed(1), y: +q[1].toFixed(1) });
        // «м. Рока», «оз. Таватуй» — the way an atlas writes a one-word name of a cape or a small lake.
        const txt = f.kind === 'peak' && f.elev ? `${name} ${new Intl.NumberFormat(LOC).format(f.elev)}` : (['cape', 'lake_pt'].includes(f.kind) ? abbrev(f.kind, name) + name : name);
        const spot = freeSpot(q[0] + 7, q[1] + 4, textW(txt, 11), 11, 'start');
        if (spot) S.items.push({ id: nid(), t: 'label', text: txt, size: 11, style, x: spot[0], y: spot[1], anchor: 'start', ref: f.id });
        else {
            const left = freeSpot(q[0] - 7, q[1] + 4, textW(txt, 11), 11, 'end');
            if (left) S.items.push({ id: nid(), t: 'label', text: txt, size: 11, style, x: left[0], y: left[1], anchor: 'end', ref: f.id });
            else numberObject(f, q[0] + 9, q[1] - 7);
        }
        return;
    }
    // Areas: the pole of inaccessibility, horizontal; ranges along their axis.
    const s = E.labelSpot(f, proj);
    if (!s || !inPanel(pn, [s.x, s.y])) { if (!quiet) toast(tr('not_on_map'), 'info'); return false; }
    // A lake the base does not draw (a small one on the map of Russia): its outline comes with the label.
    if ((f.kind === 'lake' || f.kind === 'reservoir') && (!S.layers.lakes || f.rank > b.ranks.lakes)) {
        const d = pn.path(f);
        if (d) S.items.push({ id: nid(), t: 'shape', d: roundPath(d), ref: f.id });
    }
    const big = ['ocean'].includes(f.kind), sea = ['sea', 'country', 'polity'].includes(f.kind);
    let size = Math.max(10, Math.min(big ? 17 : 15, Math.sqrt(s.area) / (big ? 10 : 12)));
    const txt = big ? name.toUpperCase() : name;
    const w = textW(txt, size, big);
    const rot = f.kind === 'range' ? Ly.principalAngle(f, proj) : 0;
    if (!rot && w / 2 > s.room * 2.4 && !(big || sea)) {
        // Too narrow to write inside (Baikal, an island): next to it, outside, the way a pupil would.
        if (['lake', 'reservoir', 'island', 'islands', 'peninsula', 'isthmus', 'delta', 'lagoon', 'strait', 'bay', 'gulf'].includes(f.kind)) {
            const b = pn.path.bounds(f), sz = 11, ww = textW(txt, sz);
            const right = freeSpot(b[1][0] + 5, s.y + 4, ww, sz, 'start');
            if (right && right[0] + ww < pn.rect[2]) { S.items.push({ id: nid(), t: 'label', text: txt, size: sz, style, x: right[0], y: right[1], anchor: 'start', ref: f.id }); return; }
            const left = freeSpot(b[0][0] - 5, s.y + 4, ww, sz, 'end');
            if (left && left[0] - ww > pn.rect[0]) { S.items.push({ id: nid(), t: 'label', text: txt, size: sz, style, x: left[0], y: left[1], anchor: 'end', ref: f.id }); return; }
        }
        return numberObject(f, s.x, s.y);
    }
    let at = [s.x, s.y + size * 0.35];
    if (!rot) {
        // A sea or a state cut by the frame (the Pacific on a world map centred on Europe): its centre
        // lies near the edge, and the name ran off the map — kept whole inside the frame.
        at[0] = Math.max(pn.rect[0] + 4 + w / 2, Math.min(pn.rect[2] - 4 - w / 2, at[0]));
        at[1] = Math.max(size + 2, Math.min(pn.rect[3] - 4, at[1]));
        const free = freeSpot(at[0], at[1], w, size, 'middle');
        if (!free && !(big || sea)) return numberObject(f, s.x, s.y);
        if (free) at = free;
    }
    S.items.push({ id: nid(), t: 'label', text: txt, size: +size.toFixed(1), style, x: at[0], y: at[1], rot: rot || 0, bold: big, ls: big ? 2 : 0, ref: f.id });
}
/* Paths from d3 carry 15 digits per number: a tenth of a unit on a 1000-wide map is enough, and
   the work (drafts, links for a class) stays small. */
function roundPath(d) { return d.replace(/-?\d+\.\d+/g, (m) => (+m).toFixed(1)); }
/* Two units of slack: a map fitted to Russia puts Cape Dezhnev — its easternmost point — 0.07 above
   the frame (the conic curves Chukotka up to the top edge), and «not on this map» was the answer. */
function inPanel(pn, q) { return q[0] >= pn.rect[0] - 2 && q[0] <= pn.rect[2] + 2 && q[1] >= -2 && q[1] <= pn.rect[3] + 2; }
/* Boxes of what is already written on the map (straight labels, symbols, numbers). */
function takenBoxes() {
    const out = [];
    for (const it of S.items) {
        if (it.t === 'label' && !it.path) {
            const w = textW(it.text, it.size, it.bold) + (it.ls || 0) * it.text.length, h = it.size * 1.1;
            const x0 = it.anchor === 'start' ? it.x : it.anchor === 'end' ? it.x - w : it.x - w / 2;
            if (it.rot && Math.abs(it.rot) > 20) { const r = Math.max(w, h) / 2; out.push([it.x - r, it.y - r, it.x + r, it.y + r]); }
            else out.push([x0, it.y - h * 0.8, x0 + w, it.y + h * 0.25]);
        } else if (it.t === 'sym' || it.t === 'num') out.push([it.x - 7, it.y - 7, it.x + 7, it.y + 7]);
    }
    return out;
}
/* The nearest free place for a straight label around (x, y): the spot itself, then
   a line above or below, then slightly aside. null — every candidate collides. */
function freeSpot(x, y, w, h, anchor) {
    const taken = takenBoxes();
    const hit = (bx, by) => {
        const x0 = anchor === 'start' ? bx : anchor === 'end' ? bx - w : bx - w / 2;
        const b = [x0, by - h * 0.8, x0 + w, by + h * 0.25];
        return taken.some((t) => b[0] < t[2] && t[0] < b[2] && b[1] < t[3] && t[1] < b[3]);
    };
    for (const [dx, dy] of [[0, 0], [0, -h * 1.1], [0, h * 1.1], [w * 0.3, 0], [-w * 0.3, 0], [0, -h * 2.2], [0, h * 2.2]]) {
        if (!hit(x + dx, y + dy)) return [x + dx, y + dy];
    }
    return null;
}
function numberObject(f, x, y) {
    const name = f.names[LANG] || f.names.en || '';
    S.items.push({ id: nid(), t: 'num', n: S.nextNum++, x: +x.toFixed(1), y: +y.toFixed(1), text: abbrev(f.kind, name) + name, ref: f.id });
}
/* «оз. Байкал», but «Ладожское озеро» stays as it is: a name of two words already
   carries its kind word, and «оз. Ладожское озеро» is what a teacher marks down. */
function abbrev(kind, name) {
    if (name && name.trim().split(/\s+/).length > 1) return '';
    const a = tr('abbr_' + kind);
    return a && a !== 'abbr_' + kind ? a + ' ' : '';
}
async function fillObject(f, color) {
    const layerName = f.layerName || Ly.KIND_LAYER[f.kind];
    const ex = S.items.find((i) => i.t === 'fill' && i.layer === layerName && String(i.ref) === String(f.id));
    if (ex) { if (ex.color === color) S.items.splice(S.items.indexOf(ex), 1); else ex.color = color; }
    else S.items.push({ id: nid(), t: 'fill', layer: layerName, ref: f.id, color });
}

// ── hit testing for the fill tool ─────────────────────────────────────────
async function areaAt(pt) {
    const g = await geometry();
    const pn = g.panels.find((p) => inPanel(p, pt));
    if (!pn || !pn.p.invert) return null;
    const ll = pn.p.invert(pt);
    if (!ll || !isFinite(ll[0])) return null;
    const b = BASES[S.base];
    const pools = [];
    // A historical map: a click colours the state of that year («закрасьте Золотую Орду»).
    if (S.layers.hist) {
        const h = (await histLayer(S.layers.hist)).find((f) => d3.geoContains(f, ll));
        if (h) return h;
    }
    // With the natural zones on, a click colours the zone: «закрасьте тайгу зелёным».
    if (S.layers.zones) {
        const z = (await zonesLayer()).find((f) => d3.geoContains(f, ll));
        if (z) return z;
    }
    if (S.political && b.political) pools.push(await layer(b.political));
    pools.push((await layer('lakes')).filter((f) => f.rank <= Math.max(6, b.ranks.lakes)));
    pools.push(await layer('regions'));
    pools.push(await layer('marine'));
    if (!S.political) pools.push(await layer(b.political || 'countries'));
    let best = null, ba = Infinity;
    for (const pool of pools) {
        for (const f of pool) {
            if (!d3.geoContains(f, ll)) continue;
            const a = d3.geoArea(f);
            if (a < ba) { ba = a; best = f; }
        }
        if (best && pool !== pools[pools.length - 1] && S.political) break;
    }
    return best;
}

// ── zoom & pan (screen only) ──────────────────────────────────────────────
const Z = { k: 1, x: 0, y: 0 };
function applyZoom() {
    const g = $('#cm-zoom');
    if (g) g.setAttribute('transform', `translate(${Z.x} ${Z.y}) scale(${Z.k})`);
    // A finger that starts on the map scrolls the page (pan-y) — unless the map owns the
    // gesture: zoomed in, drawing an arrow, or something is selected. Chromium decides at
    // touchstart and ignores touch-action on SVG children, and a page scroll that already
    // began sends only pointercancel — so a selected label is what makes it draggable.
    const svg = $('#cm-svg');
    if (svg) svg.style.touchAction = (Z.k > 1 || tool === 'arrow' || drag || selectedId) ? 'none' : 'pan-y';
    const back = $('#cm-zreset');
    if (back) back.hidden = Z.k === 1;
}
function zoomAt(f, cx, cy) {
    const k = Math.max(1, Math.min(16, Z.k * f));
    Z.x = cx - (cx - Z.x) * (k / Z.k); Z.y = cy - (cy - Z.y) * (k / Z.k); Z.k = k;
    if (Z.k === 1) { Z.x = 0; Z.y = 0; }
    applyZoom();
}
function svgPt(evt) {
    const svg = $('#cm-svg'), r = svg.getBoundingClientRect(), vb = svg.viewBox.baseVal;
    const header = S.header ? 64 : 0;
    const x = (evt.clientX - r.left) * vb.width / r.width, y = (evt.clientY - r.top) * vb.height / r.height - header;
    return [(x - Z.x) / Z.k, (y - Z.y) / Z.k];
}

// ── tools ────────────────────────────────────────────────────────────────
let drag = null, arrowPts = [];
function setTool(t) {
    tool = t;
    $$('.cm-tools .cm-tool').forEach((b) => { const on = b.dataset.tool === t; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('.cm-opt').forEach((p) => { p.hidden = p.dataset.for !== t; });
    $('#cm-svg').dataset.tool = t;
    if (t !== 'arrow') { arrowPts = []; drawDraft(); }
    if (t !== 'select') select(null);
    $('#cm-tool-hint').textContent = tr('hint_' + t);
    applyZoom();
}
function select(id) {
    selectedId = id;
    $$('#cm-svg [data-item]').forEach((el) => el.classList.toggle('cm-sel', el.getAttribute('data-item') === id));
    const it = S.items.find((i) => i.id === id);
    if (!it) selectedId = null;
    applyZoom();
    const box = $('#cm-sel-box');
    box.hidden = !it;
    if (!it) return;
    $('#cm-sel-text').hidden = !(it.t === 'label' || it.t === 'num');
    if (it.t === 'label' || it.t === 'num') $('#cm-sel-text').value = it.text;
    $('#cm-sel-size').hidden = it.t !== 'label';
}
function drawDraft() {
    const g = $('#cm-draft');
    if (!g) return;
    if (!arrowPts.length) { g.innerHTML = ''; $('#cm-arrow-done').disabled = true; return; }
    $('#cm-arrow-done').disabled = arrowPts.length < 2;
    g.innerHTML = itemSvg({ t: 'arrow', id: 'draft', style: arrowStyle, pts: arrowPts.length > 1 ? arrowPts : [arrowPts[0], [arrowPts[0][0] + 0.1, arrowPts[0][1]]] }) +
        arrowPts.map((p) => `<circle cx="${p[0]}" cy="${p[1]}" r="3.5" fill="#fff" stroke="#111827"/>`).join('');
}
function finishArrow() {
    if (arrowPts.length >= 2) { S.items.push({ id: nid(), t: 'arrow', style: arrowStyle, pts: arrowPts.map((p) => [+p[0].toFixed(1), +p[1].toFixed(1)]) }); commit(); ae('arrow', { style: arrowStyle }); }
    arrowPts = []; drawDraft(); render();
}
let textEd = null;
function openTextEditor(pt, existing) {
    closeTextEditor(false);
    const stage = $('.cm-stage'), svg = $('#cm-svg'), r = svg.getBoundingClientRect(), sr = stage.getBoundingClientRect(), vb = svg.viewBox.baseVal;
    const header = S.header ? 64 : 0;
    const sx = (pt[0] * Z.k + Z.x) * r.width / vb.width + (r.left - sr.left), sy = (pt[1] * Z.k + Z.y + header) * r.height / vb.height + (r.top - sr.top);
    const inp = document.createElement('input');
    inp.className = 'tc-in cm-text-ed';
    inp.placeholder = tr('text_ph');
    inp.style.left = Math.max(4, Math.min(sx - 70, sr.width - 180)) + 'px';
    inp.style.top = Math.max(4, sy - 18) + 'px';
    stage.appendChild(inp);
    textEd = { inp, pt };
    inp.focus({ preventScroll: true });
    inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); closeTextEditor(true); } else if (e.key === 'Escape') closeTextEditor(false); });
    inp.addEventListener('blur', () => setTimeout(() => closeTextEditor(true), 0));
}
function closeTextEditor(save) {
    if (!textEd) return;
    const { inp, pt } = textEd;
    textEd = null;
    const v = inp.value.trim();
    inp.remove();
    if (save && v) { S.items.push({ id: nid(), t: 'label', text: v, size: textSize, style: 'relief', x: pt[0], y: pt[1], anchor: 'middle' }); commit(); render(); ae('text'); firstEdit('text'); }
}
function itemAt(evt) {
    const el = evt.target.closest && evt.target.closest('[data-item]');
    return el ? el.getAttribute('data-item') : null;
}
function bindStage() {
    const svg = $('#cm-svg');
    const ptrs = new Map();
    let start = null, moved = false, pinch0 = 0;
    /* What the press landed on. The drag captures the pointer, and a captured press ends with its
       `click` aimed at the <svg> itself — so «select what the click hit» deselected the label it
       had just selected. The press, not the click, says what was pressed. */
    let downItem = null;
    svg.addEventListener('wheel', (e) => { if (!e.ctrlKey && !e.metaKey && Z.k === 1 && e.deltaY > 0) return; e.preventDefault(); const r = svg.getBoundingClientRect(), vb = svg.viewBox.baseVal; zoomAt(e.deltaY < 0 ? 1.25 : 0.8, (e.clientX - r.left) * vb.width / r.width, (e.clientY - r.top) * vb.height / r.height - (S.header ? 64 : 0)); }, { passive: false });
    svg.addEventListener('pointerdown', (e) => {
        ptrs.set(e.pointerId, [e.clientX, e.clientY]);
        if (ptrs.size === 2) { const [a, b] = [...ptrs.values()]; pinch0 = Math.hypot(a[0] - b[0], a[1] - b[1]); drag = null; return; }
        start = { x: e.clientX, y: e.clientY, zx: Z.x, zy: Z.y };
        moved = false;
        if (QZ.on) { downItem = null; return; }
        downItem = itemAt(e);
        if (tool === 'select') {
            const id = downItem;
            if (id) {
                const it = S.items.find((i) => i.id === id);
                if (it && it.t !== 'fill') { drag = { id, p0: svgPt(e), snap: JSON.stringify(it) }; select(id); svg.setPointerCapture(e.pointerId); applyZoom(); }
            }
        }
    });
    svg.addEventListener('pointermove', (e) => {
        if (!ptrs.has(e.pointerId)) return;
        ptrs.set(e.pointerId, [e.clientX, e.clientY]);
        const r = svg.getBoundingClientRect(), sc = svg.viewBox.baseVal.width / r.width;
        if (ptrs.size === 2) {
            const [a, b] = [...ptrs.values()], d = Math.hypot(a[0] - b[0], a[1] - b[1]);
            if (pinch0 > 0) { zoomAt(d / pinch0, ((a[0] + b[0]) / 2 - r.left) * sc, ((a[1] + b[1]) / 2 - r.top) * sc - (S.header ? 64 : 0)); pinch0 = d; moved = true; }
            return;
        }
        if (!start) return;
        const dx = e.clientX - start.x, dy = e.clientY - start.y;
        if (Math.hypot(dx, dy) > 5) moved = true;
        if (drag && moved) {
            const it = S.items.find((i) => i.id === drag.id), p = svgPt(e), o = JSON.parse(drag.snap);
            const ddx = p[0] - drag.p0[0], ddy = p[1] - drag.p0[1];
            moveItem(it, o, ddx, ddy);
            const el = $(`#cm-svg [data-item="${CSS.escape(it.id)}"]`);
            if (el) el.outerHTML = itemSvg(it, true);
            return;
        }
        if (moved && Z.k > 1 && (tool !== 'arrow' || QZ.on)) { Z.x = start.zx + dx * sc; Z.y = start.zy + dy * sc; applyZoom(); }
    });
    const end = (e) => {
        ptrs.delete(e.pointerId);
        if (ptrs.size < 2) pinch0 = 0;
        if (drag) { if (moved) { commit(); render(); } drag = null; applyZoom(); }
    };
    svg.addEventListener('pointerup', end); svg.addEventListener('pointercancel', end);
    svg.addEventListener('click', async (e) => {
        if (moved) { moved = false; return; }
        if (QZ.on) { if (QZ.mode === 'find' && !QZ.locked && QZ.list.length && QZ.i < QZ.list.length) quizTap(svgPt(e)); return; }
        const pt = svgPt(e);
        if (tool === 'select') { select(downItem); downItem = null; return; }
        if (tool === 'erase') { const id = itemAt(e); if (id) { removeItem(id); } return; }
        if (tool === 'text') { openTextEditor(pt); return; }
        if (tool === 'sym') { S.items.push({ id: nid(), t: 'sym', sym: symbol, x: +pt[0].toFixed(1), y: +pt[1].toFixed(1) }); commit(); render(); ae('symbol', { sym: symbol }); firstEdit('sym'); return; }
        if (tool === 'arrow') { arrowPts.push(pt); drawDraft(); return; }
        if (tool === 'fill') {
            const f = await areaAt(pt);
            if (!f) { toast(tr('fill_none'), 'info'); return; }
            await fillObject(f, fillColor); commit(); render(); ae('fill', { kind: f.kind }); firstEdit('fill');
        }
    });
    svg.addEventListener('dblclick', (e) => { if (tool === 'arrow') { e.preventDefault(); arrowPts.pop(); finishArrow(); } });
    $('#cm-zin').addEventListener('click', () => zoomAt(1.5, VB_W / 2, (G ? G.H : 500) / 2));
    $('#cm-zout').addEventListener('click', () => zoomAt(1 / 1.5, VB_W / 2, (G ? G.H : 500) / 2));
    $('#cm-zreset').addEventListener('click', () => { Z.k = 1; Z.x = 0; Z.y = 0; applyZoom(); });
    if (window.__wholeScreen) {
        const ws = window.__wholeScreen($('.cm-stage'), { key: SLUG, onChange: (on) => { $('#cm-full').setAttribute('aria-pressed', on ? 'true' : 'false'); ae('whole_screen', { on: on ? 1 : 0 }); } });
        $('#cm-full').hidden = false;
        $('#cm-full').addEventListener('click', () => ws.toggle());
    }
}
function moveItem(it, o, dx, dy) {
    if (it.t === 'arrow') { it.pts = o.pts.map((p) => [+(p[0] + dx).toFixed(1), +(p[1] + dy).toFixed(1)]); return; }
    if (it.path) {
        it.path = o.path.replace(/(-?\d+(?:\.\d+)?) (-?\d+(?:\.\d+)?)/g, (m, a, b) => `${(+a + dx).toFixed(1)} ${(+b + dy).toFixed(1)}`);
        return;
    }
    it.x = +(o.x + dx).toFixed(1); it.y = +(o.y + dy).toFixed(1);
}
function removeItem(id) {
    const i = S.items.findIndex((x) => x.id === id);
    if (i < 0) return;
    const [gone] = S.items.splice(i, 1);
    // The line or outline that came with a label (an undrawn river, the boundary) goes with its last label.
    if (gone && gone.ref != null && !S.items.some((x) => (x.t === 'label' || x.t === 'num') && String(x.ref) === String(gone.ref))) {
        for (let k = S.items.length - 1; k >= 0; k--) if ((S.items[k].t === 'line' || S.items[k].t === 'shape') && String(S.items[k].ref) === String(gone.ref)) S.items.splice(k, 1);
    }
    if (selectedId === id) select(null);
    commit(); render();
}

// ── search ───────────────────────────────────────────────────────────────
// The same four groups as «Проверь себя» (practice.js GROUPS): one place says what is «water».
const FILTERS = Object.fromEntries(Object.entries(Pr.GROUPS).map(([k, v]) => [k, new Set(v)]));
let filter = null, lastHits = [];
async function runSearch() {
    const q = $('#cm-q').value;
    const box = $('#cm-results');
    if (E.normalizeName(q).length < 2) { box.innerHTML = ''; return; }
    const idx = await ensureIndex();
    lastHits = idx(q, filter ? FILTERS[filter] : null);
    if (!lastHits.length) reportMiss(q);
    box.innerHTML = lastHits.length ? lastHits.map((h, i) => `<div class="cm-hit">
        <button type="button" class="cm-hit-name" data-show="${i}"><b>${esc(h.name)}</b><span>${esc(tr('kind_' + h.f.kind))}${h.f.elev ? ' · ' + new Intl.NumberFormat(LOC).format(h.f.elev) + ' ' + esc(tr('m')) : ''}</span></button>
        <button type="button" class="tc-btn sm" data-label="${i}">${esc(tr('do_label'))}</button>
        ${Ly.AREA_KINDS.has(h.f.kind) ? `<button type="button" class="tc-btn sm ghost" data-fill="${i}" aria-label="${esc(tr('do_fill'))}" title="${esc(tr('do_fill'))}">🖍</button>` : ''}
    </div>`).join('') : `<p class="tc-hint">${esc(tr('search_none'))}</p>`;
}
/* A query that found nothing, once it stopped changing: the list of objects pupils look for and
   the data does not have (or spells differently). The query only — a place name. */
function reportMiss(q) {
    clearTimeout(reportMiss.t);
    reportMiss.t = setTimeout(() => {
        const now = E.normalizeName($('#cm-q').value);
        if (now !== E.normalizeName(q) || lastHits.length) return;
        diag('search_miss', { q: q.trim().slice(0, 40), base: S.base, filter: filter || '' });
    }, 2500);
}
/* On a phone the map is often off the screen when a label lands (the search and the list sit
   under it): say what was written, instead of a silent tap. */
function mapOnScreen() {
    const r = $('#cm-svg').getBoundingClientRect();
    return r.bottom > 40 && r.top < (window.innerHeight || 800) - 40;
}
async function flash(f) {
    const g = await geometry();
    const out = [];
    for (const pn of g.panels) {
        const d = pn.path(f);
        if (!d) continue;
        if (f.geometry.type === 'Point') { const q = pn.p(f.geometry.coordinates); if (q && inPanel(pn, q)) out.push(`<circle cx="${q[0]}" cy="${q[1]}" r="10" fill="none" stroke="#f59e0b" stroke-width="3"/>`); }
        else out.push(`<path d="${d}" fill="${Ly.LINE_KINDS.has(f.kind) ? 'none' : '#f59e0b55'}" stroke="#f59e0b" stroke-width="${Ly.LINE_KINDS.has(f.kind) ? 3 : 1.5}"/>`);
    }
    const el = $('#cm-flash');
    if (!el) return;
    if (!out.length) { toast(tr('not_on_map'), 'info'); return; }
    el.innerHTML = `<g class="cm-flash" pointer-events="none">${out.join('')}</g>`;
    clearTimeout(flash.t);
    flash.t = setTimeout(() => { if (el.isConnected) el.innerHTML = ''; }, 2600);
}

// ── a whole assignment at once ────────────────────────────────────────────
/* «Нанесите на контурную карту: р. Волга, р. Обь, оз. Байкал, Уральские горы…» — the task is a
   list. Each line (or comma-separated item) is searched the way the search box does, the best hit
   is labelled by the same rules, and the person sees what was not found instead of guessing. */
function splitTask(text) {
    return String(text || '')
        .split(/[\n;,]+/)
        // «На карте отметь субъекты Приволжского ФО: Татарстан» — the instruction before the colon is not a name.
        .map((x) => (x.includes(':') ? x.slice(x.lastIndexOf(':') + 1) : x))
        // «1. р. Волга», «а) оз. Байкал»: the numbering goes, the kind word («р.») stays — it is the hint.
        .map((x) => x.replace(/^\s*(?:\d+[.)]|[a-zа-яё]\))\s*/i, '').replace(/[.。]+\s*$/, '').trim())
        .filter((x) => E.normalizeName(x).length >= 2);
}
/* The base an object falls on, for «it is on another map»: Russia first, then the continents, the world. */
const BOX_BASES = ['europe', 'africa', 'namerica', 'samerica', 'australia', 'antarctica', 'eurasia'];
let ruContains = null;
async function baseFor(f) {
    const c = f.geometry.type === 'Point' ? f.geometry.coordinates : d3.geoCentroid(f);
    if (!c || !isFinite(c[0])) return 'world';
    if (!ruContains) { const ru = (await layer('countries')).find((x) => x.id === 'RUS'); ruContains = ru ? Pr.makeContains(ru) : () => false; }
    const cand = [];
    if (ruContains(c)) cand.push('russia');
    for (const k of BOX_BASES) {
        const bx = BASES[k].panels[0].box;
        if (bx && c[0] >= bx[0] && c[0] <= bx[2] && c[1] >= bx[1] && c[1] <= bx[3]) cand.push(k);
    }
    cand.push('world');
    return cand.find((k) => k !== S.base) || 'world';
}
function baseName(k) { const b = $(`.cm-bases [data-base="${k}"]`); return b ? b.textContent.trim() : k; }
// ── nomenclature sets (resources/data/map-studio/sets.json) ─────────────────
/* «Что нанести на карту» из учебника — по теме и основе. Подборка хранит id объектов, а не названия:
   «Таймыр» в «Реках и озёрах» — озеро, в «Островах и полуостровах» — полуостров. В поле задания она
   встаёт названиями на языке страницы; пока текст не тронут, подписываются именно её объекты. */
const SETS = W.sets || [];
let chosenSet = null;
function syncSets() {
    const sel = $('#cm-set');
    // Rebuilt when the base changes (a switch, a restored draft, a link), not on every control sync.
    if (!sel || sel.dataset.base === S.base) return;
    sel.dataset.base = S.base;
    const mine = SETS.filter((x) => x.base === S.base || (S.base === 'hemi' && x.base === 'world'));
    sel.innerHTML = `<option value="">${esc(tr('set_none'))}</option>` + mine.map((x) => `<option value="${esc(x.key)}">${esc(tr('set_' + x.key))}</option>`).join('');
    $('#cm-set-row').hidden = !mine.length;
    chosenSet = null;
}
async function featureById() {
    const by = new Map();
    for (const f of await quizFeatures()) if (!by.has(String(f.id))) by.set(String(f.id), f);
    return by;
}
async function chooseSet(key) {
    const set = SETS.find((x) => x.key === key);
    if (!set) { chosenSet = null; return; }
    // The list comes after the layers load (a second on a phone): till then the field is empty and the
    // button waits — a quick tap labelled the previous list.
    const input = $('#cm-batch-in'), go = $('#cm-batch-go');
    input.value = '';
    go.disabled = true;
    let by;
    try { by = await featureById(); } finally { go.disabled = false; }
    const feats = set.ids.map((id) => by.get(String(id))).filter(Boolean);
    const text = feats.map((f) => f.names[LANG] || f.names.en).join(', ');
    $('#cm-batch-in').value = text;
    $('#cm-batch-out').innerHTML = '';
    chosenSet = { key, text, feats, political: !!set.political };
    ae('set', { key, n: feats.length });
}
const setInUse = () => (chosenSet && $('#cm-batch-in').value === chosenSet.text ? chosenSet : null);
/* «Проверь себя» по списку задания: объекты подборки, а для своего текста — то, что нашлось. */
async function listRefs() {
    const set = setInUse();
    if (set) return set.feats.map((f) => String(f.id));
    const idx = await ensureIndex();
    return [...new Set(splitTask($('#cm-batch-in').value).map((n) => idx(n, null)[0]).filter(Boolean).map((h) => String(h.f.id)))];
}
async function runBatch() {
    const set = setInUse();
    const queue = set ? set.feats.map((f) => ({ f })) : splitTask($('#cm-batch-in').value).slice(0, 80).map((n) => ({ n }));
    const out = $('#cm-batch-out');
    if (!queue.length) { out.innerHTML = ''; return; }
    const btn = $('#cm-batch-go');
    btn.disabled = true;
    // Subjects of a federal district are written inside their borders: the borders come on.
    if (set && set.political && !S.political) { S.political = true; syncControls(); }
    try {
        const idx = await ensureIndex();
        const done = [], missing = [], already = [], elsewhere = [];
        let asked = 0;
        while (queue.length) {
            const q = queue.shift();
            const h = q.f ? { f: q.f, name: q.f.names[LANG] || q.f.names.en } : idx(q.n, null)[0];
            const n = q.n || h.name;
            // «Саратовскую и Ульяновскую области»: two names — but only when the whole is not one
            // («Босния и Герцеговина», «Тринидад и Тобаго»).
            if (!h && /\s(и|та|and)\s/i.test(n)) { queue.unshift(...n.split(/\s(?:и|та|and)\s/i).map((x) => ({ n: x.trim() })).filter((x) => x.n)); continue; }
            asked++;
            if (!h) { missing.push(n); continue; }
            if (S.items.some((i) => (i.t === 'label' || i.t === 'num') && String(i.ref) === String(h.f.id))) { already.push(h.name); continue; }
            const before = S.items.length;
            const on = await labelObject(h.f, true);
            if (S.items.length > before) done.push(h.name);
            else if (on === false) elsewhere.push(`${h.name} (${baseName(await baseFor(h.f))})`);
            else missing.push(n);
        }
        if (done.length) { commit(); await render(); firstEdit('batch'); }
        const bits = [`<p class="tc-msg ${missing.length || elsewhere.length ? 'warn' : 'good'}">${esc(tr('batch_done', { n: done.length + already.length, of: asked }))}</p>`];
        if (elsewhere.length) bits.push(`<p class="tc-msg">${esc(tr('batch_elsewhere', { list: elsewhere.join(', ') }))}</p>`);
        if (missing.length) bits.push(`<p class="tc-msg">${esc(tr('batch_missing', { list: missing.join(', ') }))}</p>`);
        out.innerHTML = bits.join('');
        if (done.length && !mapOnScreen()) toast(tr('batch_done', { n: done.length + already.length, of: asked }), 'success');
        ae('batch', { n: asked, done: done.length, miss: missing.length, off: elsewhere.length, set: set ? set.key : '' });
        if (missing.length) diag('batch_miss', { n: missing.length, of: asked, names: missing.slice(0, 10).join('; ').slice(0, 300), base: S.base });
    } finally { btn.disabled = false; }
}

// ── side panel: legend names, items ───────────────────────────────────────
function renderItemsPanel() {
    const model = legendModel().filter((m) => m.kind !== 'num');
    $('#cm-legend-edit').innerHTML = model.length ? model.map((m) => `<label class="cm-leg-row"><span class="cm-leg-sw">${legendSwatch(m)}</span><input class="tc-in" data-leg="${esc(m.key)}" value="${esc(m.label)}" placeholder="${esc(tr('legend_ph'))}"></label>`).join('') : `<p class="tc-hint">${esc(tr('legend_empty'))}</p>`;
}
function legendSwatch(m) {
    if (m.kind === 'fill') return `<svg viewBox="0 0 16 13" width="16" height="13"><rect width="16" height="13" fill="${m.color}" stroke="#111827" stroke-width=".8"/></svg>`;
    if (m.kind === 'sym') return `<svg viewBox="-8 -8 16 16" width="18" height="18">${(SYMBOLS[m.sym] || SYMBOLS.city).draw(0, 0, m.color)}</svg>`;
    return `<svg viewBox="0 0 22 10" width="22" height="10"><path d="M1 5H20" stroke="${ARROWS[m.style] || '#111'}" stroke-width="2.2"/></svg>`;
}

// ── history, drafts, share ────────────────────────────────────────────────
function snapshot() { return JSON.stringify(S); }
function commit(counted = true) {
    if (hist.lock) return;
    const s = snapshot();
    if (hist.stack[hist.pos] === s) return;
    hist.stack.splice(hist.pos + 1);
    hist.stack.push(s);
    if (hist.stack.length > 150) hist.stack.shift();
    hist.pos = hist.stack.length - 1;
    syncUndo();
    syncShare();
    if (drafts) drafts.touch(counted);
}
function syncUndo() { $('#cm-undo').disabled = hist.pos <= 0; $('#cm-redo').disabled = hist.pos >= hist.stack.length - 1; }
function restore(s) {
    const base = S.base;
    hist.lock = true; try { S = Object.assign(freshState(), JSON.parse(s)); } finally { hist.lock = false; }
    // Undo across a change of base: the search index is built per base (subjects only on Russia).
    if (S.base !== base) { index = null; G = null; baseSvg.key = ''; }
    syncControls(); render();
}
function undo() { if (hist.pos > 0) { hist.pos--; restore(hist.stack[hist.pos]); syncUndo(); if (drafts) drafts.touch(true); } }
function redo() { if (hist.pos < hist.stack.length - 1) { hist.pos++; restore(hist.stack[hist.pos]); syncUndo(); if (drafts) drafts.touch(true); } }
function contentKey() {
    const s = snapshot();
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return 'map:' + (h >>> 0).toString(36);
}

// ── export (shared with the chart maker) ──────────────────────────────────
function exportSvgText() {
    const src = $('#cm-svg'), svg = src.cloneNode(true);
    const z = svg.querySelector('#cm-zoom');
    if (z) z.removeAttribute('transform');
    svg.querySelectorAll('#cm-flash, #cm-draft').forEach((n) => n.remove());
    svg.querySelectorAll('.cm-sel').forEach((n) => n.classList.remove('cm-sel'));
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const vb = src.viewBox.baseVal;
    svg.setAttribute('width', String(vb.width)); svg.setAttribute('height', String(vb.height));
    return '<?xml version="1.0" encoding="UTF-8"?>\n' + new XMLSerializer().serializeToString(svg);
}
async function rasterize(side) {
    const vb = $('#cm-svg').viewBox.baseVal;
    return rasterizeSvg(exportSvgText(), vb.width, vb.height, side, (got) => {
        toast(tr('png_clamped', { px: got }), 'info');
        ae('png_clamped', { want: side, got });
    });
}
function fileBase() {
    const t = (S.title || tr('title_' + S.base)).trim();
    try { return window.__outputName ? window.__outputName.sanitize(t) : t; } catch (e) { return 'map'; }
}
async function doExport(fmt) {
    select(null);
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, contentKey()) : null;
    if (window.__toolLimit && !run) return;
    stage('export', { fmt });
    try {
        let ok;
        if (fmt === 'pdf') {
            const mod = await import('./pdf.js');
            const clone = $('#cm-svg').cloneNode(true);
            const z = clone.querySelector('#cm-zoom'); if (z) z.removeAttribute('transform');
            clone.querySelectorAll('#cm-flash, #cm-draft').forEach((n) => n.remove());
            // pdf.js measures curved labels on live paths: keep the clone in the document while it runs.
            clone.style.cssText = 'position:absolute;left:-99999px;top:0;width:1000px;height:auto';
            document.body.appendChild(clone);
            const pages = [clone];
            const key = S.numbered ? answerKeySvg(Pr.numberedView(S.items, textW)) : null;
            if (key) { key.style.cssText = clone.style.cssText; document.body.appendChild(key); pages.push(key); }
            try { ok = await window.__downloadFile(await mod.mapPdf(pages, { title: S.title, paper: $('#cm-paper').value, assets: W.assets || {} }), fileBase() + (S.numbered ? '-' + tr('file_test') : '') + '.pdf'); }
            finally { clone.remove(); if (key) key.remove(); }
        } else if (fmt === 'svg') ok = await window.__downloadFile(new Blob([exportSvgText()], { type: 'image/svg+xml' }), fileBase() + '.svg');
        else ok = await window.__downloadFile(await rasterize(+$('#cm-size').value || 2000), fileBase() + '.png');
        if (ok === false) { run && run.abort(); return; }
        run && run.commit();
        if (drafts) drafts.exported();
        try { window.__aeToolComplete && window.__aeToolComplete({ fmt, base: S.base, items: S.items.length, bw: S.bw ? 1 : 0, test: S.numbered ? 1 : 0 }); } catch (e) { /* optional */ }
    } catch (e) {
        run && run.abort();
        try { window.__handleError ? window.__handleError(e, 'contour_export', { say: tr('export_fail') }) : toast(tr('export_fail'), 'error'); } catch (_) { toast(tr('export_fail'), 'error'); }
    }
}

// ── «Проверь себя» ────────────────────────────────────────────────────────
/* A blank map (the person's labels and fills hidden): find the object by a tap, or name the
   highlighted one out of four. One try per question — the answer is shown on the map either way,
   and the mistakes can be gone over again. A teacher's link (#q=) brings its own list. */
const QZ = { on: false, mode: 'find', src: 'water', count: 10, list: [], pool: [], i: 0, right: 0, wrong: [], t0: 0, locked: false, task: null, opts: [] };
async function quizFeatures() {
    const names = ['rivers', 'lakes', 'marine', 'regions', 'points', 'cities', 'lines', 'countries'];
    if (S.base === 'russia') names.push('subjects');
    return (await Promise.all(names.map(layer))).flat().concat(await histLayer(S.layers.hist));
}
function rankOk(f, b) {
    if (f.kind === 'polity') return !!S.layers.hist;
    const L = Ly.KIND_LAYER[f.kind];
    if (!L || L === 'lines') return false;
    if (L === 'countries') return b.political === 'countries';
    if (L === 'subjects') return S.base === 'russia';
    const lim = b.ranks[L];
    return lim != null && lim > 0 && (f.rank ?? 9) <= lim;
}
function onBase(f, g) {
    for (const pn of g.panels) {
        let inside = 0;
        for (const line of Pr.projectedLines(f, pn.p)) for (const q of line) if (inPanel(pn, q) && ++inside >= 2) return true;
        if (inside && f.geometry.type === 'Point') return true;
    }
    return false;
}
let focusFn = { base: '', fn: null };
const poolCache = new Map();
async function quizPool(src, refs) {
    const ck = !refs && src !== 'mine' ? S.base + '|' + (S.political ? 1 : 0) + '|' + src : '';
    if (ck && poolCache.has(ck)) return poolCache.get(ck);
    const out = await buildPool(src, refs);
    if (ck) poolCache.set(ck, out);
    return out;
}
async function buildPool(src, refs) {
    const g = await geometry(), b = BASES[S.base];
    const feats = await quizFeatures();
    let cand;
    if (refs || src === 'mine') {
        const byId = new Map();
        for (const f of feats) if (!byId.has(String(f.id))) byId.set(String(f.id), f);
        const want = refs || [...new Set(S.items.filter((i) => i.ref != null).map((i) => String(i.ref)))];
        cand = want.map((r) => byId.get(String(r))).filter(Boolean);
    } else {
        cand = feats.filter((f) => Pr.groupOf(f.kind) === src && rankOk(f, b));
        // A one-country base asks about that country, not about the neighbours drawn grey.
        // The country is laid out in 1° cells once per base (~0.3 s on a desktop, ~1 s on a phone).
        if (b.focus) {
            if (focusFn.base !== S.base) {
                const focus = (await layer('countries')).find((f) => f.id === b.focus);
                focusFn = { base: S.base, fn: focus ? Pr.focusFilter(focus, d3) : null };
            }
            if (focusFn.fn) cand = cand.filter((f) => f.kind !== 'country' && focusFn.fn(f));
        }
    }
    const out = [], seen = new Set();
    for (const f of cand) {
        const name = f.names[LANG] || f.names.en || '';
        if (!name || seen.has(name) || !onBase(f, g)) continue;
        seen.add(name);
        out.push({ f, name });
    }
    return out;
}
function quizEl(id) { return $('#cm-quiz-' + id); }
async function startQuiz(opts = {}) {
    const mode = opts.mode || QZ.mode, src = opts.src || QZ.src;
    const go = $('#cm-quiz-go');
    go.disabled = true; go.setAttribute('aria-busy', 'true');
    let pool;
    try { pool = opts.list ? opts.pool : await quizPool(src, opts.refs || (src === 'list' ? await listRefs() : null)); }
    finally { go.disabled = false; go.removeAttribute('aria-busy'); }
    if (!pool.length) { toast(tr(src === 'mine' ? 'quiz_mine_empty' : src === 'list' ? 'quiz_list_empty' : 'quiz_empty'), 'info'); return; }
    clearTimeout(answer.t);
    const n = opts.list ? opts.list.length : (QZ.count > 0 ? Math.min(QZ.count, pool.length) : pool.length);
    Object.assign(QZ, { on: true, mode, src, pool, list: opts.list ? Pr.shuffle(opts.list) : Pr.shuffle(pool).slice(0, n), i: 0, right: 0, wrong: [], t0: Date.now(), locked: false });
    select(null);
    arrowPts = []; drawDraft();
    $('#cm').classList.add('cm-quizzing');
    quizEl('box').hidden = false;
    quizEl('res').hidden = true;
    quizEl('run').hidden = false;
    await render();
    const m = $('.cm-p-map');
    if (m) m.scrollIntoView({ block: 'nearest' });
    ask();
    stage('quiz', { mode, src, n: QZ.list.length });
    ae('quiz_start', { mode, src, n: QZ.list.length, teacher: QZ.task ? 1 : 0 });
}
function stopQuiz() {
    QZ.on = false; QZ.list = []; QZ.locked = false;
    $('#cm').classList.remove('cm-quizzing');
    quizEl('box').hidden = true;
    render();
}
async function markObject(f, color, fill) {
    const g = await geometry(), el = $('#cm-qz');
    if (!el) return;
    const out = [];
    for (const pn of g.panels) {
        if (f.geometry.type === 'Point') { const q = pn.p(f.geometry.coordinates); if (q && inPanel(pn, q)) out.push(`<circle cx="${q[0]}" cy="${q[1]}" r="9" fill="${fill ? color + '55' : 'none'}" stroke="${color}" stroke-width="3"/>`); continue; }
        const d = pn.path(f);
        if (d) out.push(`<path d="${d}" fill="${Ly.LINE_KINDS.has(f.kind) || !fill ? 'none' : color + '55'}" stroke="${color}" stroke-width="${Ly.LINE_KINDS.has(f.kind) ? 3.2 : 1.6}" stroke-linejoin="round"/>`);
    }
    el.innerHTML += out.join('');
}
function clearMarks() { const el = $('#cm-qz'); if (el) el.innerHTML = ''; }
/* The kind under the name, unless the name already says it: «Куйбышевское водохранилище», not
   «… водохранилище водохранилище»; «Волга» still gets «река». */
function kindNote(name, kind) {
    const label = tr('kind_' + kind);
    if (!label || label === 'kind_' + kind) return '';
    const words = new Set(String(name).toLowerCase().split(/[\s\-\u2010-\u2014,()]+/).filter(Boolean));
    return label.toLowerCase().split(/[\s,]+/).some((w) => w.length >= 3 && words.has(w)) ? '' : label;
}
function ask() {
    const q = QZ.list[QZ.i];
    clearMarks();
    QZ.locked = false;
    quizEl('say').textContent = '';
    quizEl('say').className = 'cm-quiz-say';
    quizEl('p').textContent = tr('quiz_progress', { i: QZ.i + 1, n: QZ.list.length, ok: QZ.right });
    const opts = quizEl('opts');
    if (QZ.mode === 'find') {
        quizEl('q').innerHTML = esc(tr('quiz_find_q', { name: '\u0001' })).replace('\u0001', `<b>${esc(q.name)}</b>`) + (kindNote(q.name, q.f.kind) ? ` <span>${esc(kindNote(q.name, q.f.kind))}</span>` : '');
        opts.hidden = true; opts.innerHTML = '';
    } else {
        quizEl('q').textContent = tr('quiz_name_q');
        markObject(q.f, '#f59e0b', true);
        QZ.opts = Pr.shuffle([q, ...Pr.distractors(q, QZ.pool, 3)]);
        opts.innerHTML = QZ.opts.map((o, i) => `<button type="button" class="tc-btn ghost cm-quiz-o" data-o="${i}">${esc(o.name)}</button>`).join('');
        opts.hidden = false;
    }
}
function quizTap(pt) {
    const q = QZ.list[QZ.i];
    const svg = $('#cm-svg'), r = svg.getBoundingClientRect(), vb = svg.viewBox.baseVal;
    // 14 screen pixels, whatever the zoom: a river on a phone is a hairline.
    const tol = 14 * (vb.width / r.width) / Z.k;
    const panels = G ? G.panels : [];
    const ok = panels.some((pn) => inPanel(pn, pt) && Pr.hits(q.f, pt, pn, tol, d3));
    if (!ok) {
        const el = $('#cm-qz');
        if (el) el.innerHTML += `<path d="M${pt[0] - 6} ${pt[1] - 6}L${pt[0] + 6} ${pt[1] + 6}M${pt[0] + 6} ${pt[1] - 6}L${pt[0] - 6} ${pt[1] + 6}" stroke="#dc2626" stroke-width="3" stroke-linecap="round"/>`;
    }
    answer(ok);
}
function answer(ok, picked) {
    const q = QZ.list[QZ.i];
    QZ.locked = true;
    const say = quizEl('say');
    if (ok) { QZ.right++; say.textContent = tr('quiz_right'); say.className = 'cm-quiz-say good'; markObject(q.f, '#16a34a', true); }
    else {
        QZ.wrong.push(q);
        say.textContent = QZ.mode === 'name' ? tr('quiz_wrong_name', { name: q.name }) : tr('quiz_wrong');
        say.className = 'cm-quiz-say bad';
        markObject(q.f, '#f59e0b', true);
    }
    if (QZ.mode === 'name') $$('.cm-quiz-o').forEach((b, i) => { b.disabled = true; if (QZ.opts[i] === q) b.classList.add('good'); else if (i === picked) b.classList.add('bad'); });
    quizEl('p').textContent = tr('quiz_progress', { i: QZ.i + 1, n: QZ.list.length, ok: QZ.right });
    clearTimeout(answer.t);
    answer.t = setTimeout(nextQuestion, ok ? 900 : 2200);
}
function nextQuestion() {
    if (!QZ.on) return;
    QZ.i++;
    if (QZ.i >= QZ.list.length) finishQuiz(); else ask();
}
function finishQuiz() {
    clearMarks();
    QZ.locked = true;
    const sec = Math.round((Date.now() - QZ.t0) / 1000);
    const time = `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
    quizEl('run').hidden = true;
    const res = quizEl('res');
    const bits = [`<p class="cm-quiz-score">${esc(tr('quiz_result', { ok: QZ.right, n: QZ.list.length, time }))}</p>`];
    if (QZ.wrong.length) {
        bits.push(`<p class="tc-f-l">${esc(tr('quiz_mistakes'))}</p><div class="cm-quiz-miss">` +
            QZ.wrong.map((q, i) => `<button type="button" class="tc-chip" data-miss="${i}">${esc(q.name)}</button>`).join('') + '</div>');
    }
    bits.push(`<div class="tc-actions cm-mt"><button type="button" class="tc-btn sm" data-qa="again">${esc(tr('quiz_again'))}</button>` +
        (QZ.wrong.length ? `<button type="button" class="tc-btn sm ghost" data-qa="redo">${esc(tr('quiz_redo'))}</button>` : '') +
        `<button type="button" class="tc-btn sm ghost" data-qa="close">${esc(tr('quiz_close'))}</button></div>`);
    res.innerHTML = bits.join('');
    res.hidden = false;
    ae('quiz_done', { mode: QZ.mode, src: QZ.src, n: QZ.list.length, ok: QZ.right, sec, teacher: QZ.task ? 1 : 0 });
    try { window.__aeToolComplete && window.__aeToolComplete({ quiz: 1, mode: QZ.mode, n: QZ.list.length, ok: QZ.right, base: S.base }); } catch (e) { /* optional */ }
}
function showTask(task) {
    QZ.task = task;
    QZ.mode = task.mode;
    $('#cm').classList.add('cm-quizzing');
    quizEl('box').hidden = false;
    quizEl('run').hidden = true;
    quizEl('res').hidden = false;
    quizEl('res').innerHTML = `<p class="cm-quiz-score">${esc(tr(task.mode === 'name' ? 'quiz_task_name' : 'quiz_task', { n: task.refs.length }))}</p>` +
        `<div class="tc-actions"><button type="button" class="tc-btn" data-qa="task">${esc(tr('quiz_go'))}</button><button type="button" class="tc-btn sm ghost" data-qa="close">${esc(tr('quiz_close'))}</button></div>`;
}
function syncQuizSet() {
    $$('.cm-qmode .tc-seg-b').forEach((b) => { const on = b.dataset.qmode === QZ.mode; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('.cm-qsrc .tc-chip').forEach((b) => { const on = b.dataset.qsrc === QZ.src; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $$('.cm-qn .tc-chip').forEach((b) => { const on = +b.dataset.qn === QZ.count; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
}
function bindQuiz() {
    $$('.cm-qmode .tc-seg-b').forEach((b) => b.addEventListener('click', () => { QZ.mode = b.dataset.qmode; syncQuizSet(); }));
    $$('.cm-qsrc .tc-chip').forEach((b) => b.addEventListener('click', () => { QZ.src = b.dataset.qsrc; syncQuizSet(); }));
    $$('.cm-qn .tc-chip').forEach((b) => b.addEventListener('click', () => { QZ.count = +b.dataset.qn; syncQuizSet(); }));
    $('#cm-quiz-go').addEventListener('click', () => { QZ.task = null; startQuiz(); });
    $('#cm-quiz-skip').addEventListener('click', () => { if (QZ.on && !QZ.locked) answer(false); });
    // Stopping early scores what was answered, not the whole list.
    $('#cm-quiz-stop').addEventListener('click', () => {
        clearTimeout(answer.t);
        const done = QZ.i + (QZ.locked ? 1 : 0);
        if (done > 0 && QZ.list.length) { QZ.list = QZ.list.slice(0, done); finishQuiz(); } else stopQuiz();
    });
    $('#cm-quiz-opts').addEventListener('click', (e) => {
        const b = e.target.closest('[data-o]');
        if (!b || QZ.locked) return;
        const i = +b.dataset.o;
        answer(QZ.opts[i] === QZ.list[QZ.i], i);
    });
    $('#cm-quiz-res').addEventListener('click', (e) => {
        const m = e.target.closest('[data-miss]'), a = e.target.closest('[data-qa]');
        if (m) { clearMarks(); markObject(QZ.wrong[+m.dataset.miss].f, '#f59e0b', true); return; }
        if (!a) return;
        const what = a.dataset.qa;
        if (what === 'close') { QZ.task = null; stopQuiz(); return; }
        if (what === 'task') { startQuiz({ mode: QZ.task.mode, src: 'task', refs: QZ.task.refs }); return; }
        if (what === 'again') { startQuiz({ mode: QZ.mode, src: QZ.src, list: QZ.list, pool: QZ.pool }); return; }
        if (what === 'redo') startQuiz({ mode: QZ.mode, src: QZ.src, list: QZ.wrong.slice(), pool: QZ.pool });
    });
    syncQuizSet();
}

// ── the test variant's key sheet ───────────────────────────────────────────
function answerKeySvg(nums) {
    if (!nums.length) return null;
    const per = Math.ceil(nums.length / 2), lineH = 30, top = 110;
    // A4 portrait (1 : 1.414): the key prints as a page of its own, not a landscape strip.
    const H = Math.max(1414, top + per * lineH + 40);
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';
    const el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    el.setAttribute('viewBox', `0 0 ${VB_W} ${H}`);
    const parts = [`<rect width="${VB_W}" height="${H}" fill="#ffffff"/>`,
        `<text x="${VB_W / 2}" y="44" text-anchor="middle" font-family='${FONT}' font-size="26" font-weight="700" fill="#111827">${esc(tr('key_title', { title: S.title || tr('title_' + S.base) }))}</text>`,
        `<text x="${VB_W / 2}" y="74" text-anchor="middle" font-family='${FONT}' font-size="15" fill="#475569">${esc(tr('key_note'))}</text>`];
    nums.forEach((n, i) => {
        const col = i < per ? 0 : 1, row = i < per ? i : i - per;
        const x = rtl ? VB_W - 40 - col * 470 : 40 + col * 470, y = top + row * lineH;
        parts.push(`<text x="${x}" y="${y}"${rtl ? ' direction="rtl"' : ''} font-family='${FONT}' font-size="17" fill="#111827">${esc(n.n + '. ' + n.text)}</text>`);
    });
    el.innerHTML = parts.join('');
    return el;
}

// ── links for a class ──────────────────────────────────────────────────────
/* «Ссылка на эту карту»: the class opens it as a work of its own (the name line empty).
   «Ссылка-самопроверка»: only the base and the objects the teacher labelled — the class gets
   «Проверь себя» over exactly them. lz-string keeps a 15-label map near 3 KB of address. */
function quizRefs() { return [...new Set(S.items.filter((i) => i.ref != null).map((i) => String(i.ref)))]; }
function syncShare() { const b = $('#cm-share-quiz'); if (b) b.disabled = !quizRefs().length; }
function linkFor(kind) {
    const LZ = window.LZString;
    if (!LZ) return null;
    const base = location.origin + location.pathname;
    if (kind === 'map') { const o = JSON.parse(snapshot()); o.student = ''; return base + '#m=' + LZ.compressToEncodedURIComponent(JSON.stringify(o)); }
    return base + '#q=' + LZ.compressToEncodedURIComponent(JSON.stringify({ v: 2, b: S.base, p: S.political ? 1 : 0, m: QZ.mode, t: S.title || '', r: quizRefs() }));
}
function shareLink(kind) {
    const url = linkFor(kind);
    if (!url) { toast(tr('copy_fail'), 'error'); return; }
    if (kind === 'quiz' && !quizRefs().length) { toast(tr('share_quiz_none'), 'info'); return; }
    const sheet = navigator.share && window.matchMedia('(pointer:coarse)').matches;
    ae('share', { kind, how: sheet ? 'sheet' : 'copy', n: kind === 'quiz' ? quizRefs().length : S.items.length });
    if (sheet) { navigator.share({ title: document.title, url }).catch(() => { /* closed */ }); return; }
    const byHand = () => { try { window.prompt(tr('link_label'), url); } catch (e) { toast(tr('copy_fail'), 'error'); } };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(() => toast(tr(kind === 'quiz' ? 'link_quiz_copied' : 'link_copied'), 'success'), byHand);
    else byHand();
}
function fromHash() {
    const LZ = window.LZString;
    const m = location.hash.match(/#([mq])=([^&]+)/);
    if (!m || !LZ) return null;
    try {
        const o = JSON.parse(LZ.decompressFromEncodedURIComponent(m[2]) || 'null');
        if (!o || typeof o !== 'object') return null;
        if (m[1] === 'm') {
            if (!BASES[o.base]) return null;
            S = Object.assign(freshState(o.base), E.upgradeContourState(o), { student: '' });
            return { kind: 'map' };
        }
        if (!BASES[o.b]) return null;
        S = freshState(o.b);
        S.political = !!o.p;
        if (o.t) S.title = String(o.t).slice(0, 120);
        const refs = (Array.isArray(o.r) ? o.r : []).slice(0, 200).map((r) => String(o.v >= 2 ? r : E.legacyRegionCode(r)));
        return refs.length ? { kind: 'quiz', task: { refs, mode: o.m === 'name' ? 'name' : 'find' } } : null;
    } catch (e) { return null; }
}

// ── controls ─────────────────────────────────────────────────────────────
function syncControls() {
    $$('.cm-bases .tc-chip').forEach((b) => { const on = b.dataset.base === S.base; b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    $('#cm-political').checked = !!S.political;
    $('#cm-political-wrap').hidden = !BASES[S.base].political;
    for (const k of Object.keys(S.layers)) { const el = $('#cm-l-' + k); if (el) el.checked = !!S.layers[k]; }
    $('#cm-zones').value = S.layers.zones || '';
    $('#cm-hist').value = S.layers.hist || '';
    $('#cm-hist-label').hidden = !S.layers.hist;
    $('#cm-bw').checked = !!S.bw;
    $('#cm-header').checked = !!S.header;
    $('#cm-title').value = S.title || '';
    $('#cm-student').value = S.student || '';
    $('#cm-paper').value = $('#cm-paper').value || (W.paper || 'a4');
    $('#cm-numbered').checked = !!S.numbered;
    syncShare();
    syncSets();
}
async function setBase(b) {
    if (QZ.on) stopQuiz();
    if (S.items.length && !confirm(tr('confirm_base'))) { syncControls(); return; }
    S = freshState(b);
    G = null; baseSvg.key = ''; index = null; Z.k = 1; Z.x = 0; Z.y = 0;
    syncControls();
    await render();
    commit();
    ae('base', { base: b });
}
function bind() {
    $$('svg[data-draw]').forEach((el) => { el.innerHTML = (SYMBOLS[el.dataset.draw] || SYMBOLS.city).draw(0, 0); });
    $$('.cm-bases .tc-chip').forEach((b) => b.addEventListener('click', () => { if (b.dataset.base !== S.base) setBase(b.dataset.base); }));
    $('#cm-political').addEventListener('change', (e) => { S.political = e.target.checked; render(); commit(); });
    for (const k of ['rivers', 'lakes', 'grat', 'glines', 'coast']) { const el = $('#cm-l-' + k); if (el) el.addEventListener('change', (e) => { S.layers[k] = e.target.checked; render(); commit(); }); }
    $('#cm-hist').addEventListener('change', async (e) => {
        S.layers.hist = e.target.value || '';
        const sel = e.target;
        sel.disabled = true;
        try { if (S.layers.hist) await histLayer(S.layers.hist); } finally { sel.disabled = false; }
        index = null; poolCache.clear();
        $('#cm-hist-label').hidden = !S.layers.hist;
        await render(); commit(); ae('hist', { year: S.layers.hist || 'off' });
    });
    // An answer key in one tap: every state of the year large enough to read on this map.
    $('#cm-hist-label').addEventListener('click', async () => {
        const g = await geometry();
        const states = (await histLayer(S.layers.hist)).map((f) => ({ f, a: Math.max(...g.panels.map((pn) => pn.path.area(f) || 0)) }))
            .filter((x) => x.a > 300 && !S.items.some((i) => String(i.ref) === String(x.f.id))).sort((x, y) => y.a - x.a).slice(0, 40);
        let n = 0;
        const rect = g.panels[0].rect;
        for (const x of states) {
            const before = S.items.length;
            await labelObject(x.f, true);
            // A state reaching past the frame (the Holy Roman Empire on a map of Eastern Europe) gets its
            // name cut at the edge: such a label is not kept — the state stays for the pupil to name.
            const added = S.items.slice(before);
            const cut = added.some((it) => it.t === 'label' && !it.path && (() => { const w = textW(it.text, it.size, it.bold); const x0 = it.anchor === 'start' ? it.x : it.anchor === 'end' ? it.x - w : it.x - w / 2; return x0 < rect[0] + 2 || x0 + w > rect[2] - 2 || it.y < 12 || it.y > rect[3] - 4; })());
            if (cut) { S.items.splice(before); continue; }
            if (added.length) n++;
        }
        if (n) { commit(); await render(); firstEdit('hist_label'); }
        toast(tr('hist_labelled', { n }), 'success');
        ae('hist_label', { year: S.layers.hist, n });
    });
    $('#cm-zones').addEventListener('change', async (e) => {
        S.layers.zones = e.target.value || '';
        const sel = e.target;
        sel.disabled = true;
        try { if (S.layers.zones) await zonesLayer(); } finally { sel.disabled = false; }
        await render(); commit(); ae('zones', { mode: S.layers.zones || 'off' });
    });
    $('#cm-bw').addEventListener('change', (e) => { S.bw = e.target.checked; render(); commit(); });
    $('#cm-header').addEventListener('change', (e) => { S.header = e.target.checked; render(); commit(); });
    $('#cm-numbered').addEventListener('change', (e) => { S.numbered = e.target.checked; select(null); render(); commit(); ae('numbered', { on: S.numbered ? 1 : 0 }); });
    $('#cm-share-map').addEventListener('click', () => shareLink('map'));
    $('#cm-share-quiz').addEventListener('click', () => shareLink('quiz'));
    bindQuiz();
    $('#cm-title').addEventListener('input', (e) => { S.title = e.target.value; render(); });
    $('#cm-student').addEventListener('input', (e) => { S.student = e.target.value; render(); });
    ['#cm-title', '#cm-student'].forEach((s) => $(s).addEventListener('change', () => commit()));
    $$('.cm-tools .cm-tool').forEach((b) => b.addEventListener('click', () => setTool(b.dataset.tool)));
    $$('.cm-syms [data-sym]').forEach((b) => b.addEventListener('click', () => { symbol = b.dataset.sym; $$('.cm-syms [data-sym]').forEach((x) => x.classList.toggle('on', x === b)); }));
    $$('.cm-arrows [data-arrow]').forEach((b) => b.addEventListener('click', () => { arrowStyle = b.dataset.arrow; $$('.cm-arrows [data-arrow]').forEach((x) => x.classList.toggle('on', x === b)); drawDraft(); }));
    $$('.cm-colors [data-color]').forEach((b) => b.addEventListener('click', () => { fillColor = b.dataset.color; $$('.cm-colors [data-color]').forEach((x) => x.classList.toggle('on', x === b)); }));
    $$('.cm-sizes [data-size]').forEach((b) => b.addEventListener('click', () => { textSize = +b.dataset.size; $$('.cm-sizes [data-size]').forEach((x) => x.classList.toggle('on', x === b)); }));
    $('#cm-arrow-done').addEventListener('click', finishArrow);
    $('#cm-arrow-undo').addEventListener('click', () => { arrowPts.pop(); drawDraft(); });
    // selection box
    $('#cm-sel-del').addEventListener('click', () => { if (selectedId) removeItem(selectedId); });
    // While something is selected the map owns a finger (so the label can be dragged); «Done» gives it back to the page.
    $('#cm-sel-done').addEventListener('click', () => select(null));
    $('#cm-batch-go').addEventListener('click', runBatch);
    $('#cm-set').addEventListener('change', (e) => chooseSet(e.target.value));
    syncSets();
    $('#cm-sel-text').addEventListener('input', (e) => { const it = S.items.find((i) => i.id === selectedId); if (it) { it.text = e.target.value; render(); } });
    $('#cm-sel-text').addEventListener('change', () => commit());
    $('#cm-sel-bigger').addEventListener('click', () => { const it = S.items.find((i) => i.id === selectedId); if (it && it.size) { it.size = Math.min(40, it.size + 1); commit(); render(); } });
    $('#cm-sel-smaller').addEventListener('click', () => { const it = S.items.find((i) => i.id === selectedId); if (it && it.size) { it.size = Math.max(7, it.size - 1); commit(); render(); } });
    $('#cm-sel-rot').addEventListener('click', () => { const it = S.items.find((i) => i.id === selectedId); if (it && it.t === 'label' && !it.path) { it.rot = (((it.rot || 0) + 15 + 90) % 180) - 90; commit(); render(); } });
    // search
    let st = 0;
    $('#cm-q').addEventListener('input', () => { clearTimeout(st); st = setTimeout(runSearch, 180); });
    $$('.cm-filters .tc-chip').forEach((b) => b.addEventListener('click', () => {
        filter = filter === b.dataset.filter ? null : b.dataset.filter;
        $$('.cm-filters .tc-chip').forEach((x) => { const on = x.dataset.filter === filter; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        runSearch();
    }));
    $('#cm-results').addEventListener('click', async (e) => {
        const s = e.target.closest('[data-show]'), l = e.target.closest('[data-label]'), f = e.target.closest('[data-fill]');
        if (s) { flash(lastHits[+s.dataset.show].f); ae('search_show'); }
        if (l) {
            const h = lastHits[+l.dataset.label];
            const on = await labelObject(h.f, true);
            if (on === false) { toast(tr('not_on_map_base', { base: baseName(await baseFor(h.f)) }), 'info'); return; }
            commit(); await render(); flash(h.f);
            if (!mapOnScreen()) toast(tr('labelled', { name: h.name }), 'success');
            ae('search_label', { kind: h.f.kind }); firstEdit('search');
        }
        if (f) {
            const h = lastHits[+f.dataset.fill];
            await fillObject(h.f, fillColor); commit(); await render();
            if (!mapOnScreen()) toast(tr('filled', { name: h.name }), 'success');
            ae('search_fill', { kind: h.f.kind }); firstEdit('search');
        }
    });
    // legend names
    $('#cm-legend-edit').addEventListener('input', (e) => { const k = e.target.dataset.leg; if (k) { S.legend[k] = e.target.value; render(); } });
    $('#cm-legend-edit').addEventListener('change', () => commit());
    // history, export, clear
    $('#cm-undo').addEventListener('click', undo);
    $('#cm-redo').addEventListener('click', redo);
    $('#cm-clear').addEventListener('click', () => { if (!S.items.length || !confirm(tr('confirm_clear'))) return; S.items = []; S.nextNum = 1; select(null); commit(); render(); });
    document.addEventListener('keydown', (e) => {
        if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); }
        else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) { e.preventDefault(); removeItem(selectedId); }
        else if (e.key === 'Enter' && tool === 'arrow') { e.preventDefault(); finishArrow(); }
        else if (e.key === 'Escape') { arrowPts = []; drawDraft(); select(null); }
    });
    $$('[data-export]').forEach((b) => b.addEventListener('click', () => doExport(b.dataset.export)));
    bindStage();
}
function draftBoot() {
    if (!window.__toolDraft || !window.__toolDraft.editor) return;
    drafts = window.__toolDraft.editor({
        slug: SLUG, root: 'cmDrafts', icon: '🗺️', locale: LANG, debounceMs: 600,
        payload: () => ({ state: S, label: (S.title || tr('title_' + S.base)) + (S.student ? ' — ' + S.student : '') }),
        apply: (d) => { S = Object.assign(freshState(), E.upgradeContourState(d.state)); G = null; baseSvg.key = ''; index = null; syncControls(); return render().then(() => { hist.stack = [snapshot()]; hist.pos = 0; syncUndo(); return []; }); },
        fresh: () => { S = freshState(); G = null; baseSvg.key = ''; index = null; syncControls(); render().then(() => { hist.stack = [snapshot()]; hist.pos = 0; syncUndo(); }); },
        // A teacher's task on the page: no saved work may replace the map under it.
        started: () => hist.pos > 0 || !!QZ.task,
        t: (k, f) => (I['drafts_' + k] != null ? I['drafts_' + k] : f),
        track: (a, d) => ae('draft_' + a, d), toast,
    });
    return drafts.boot();
}
async function boot() {
    if (!d3) { toast(tr('load_fail'), 'error'); return; }
    const linked = fromHash();
    bind();
    syncControls();
    setTool('select');
    const busy = $('#cm-busy');
    try { await render(); } catch (e) {
        try { window.__handleError ? window.__handleError(e, 'contour_load', { say: tr('load_fail') }) : toast(tr('load_fail'), 'error'); } catch (_) { /* none */ }
    } finally { busy.hidden = true; }
    hist.stack = [snapshot()]; hist.pos = 0; syncUndo();
    if (linked && linked.kind === 'quiz') showTask(linked.task);
    draftBoot();
    if (linked && linked.kind === 'map' && drafts && drafts.adopt) {
        drafts.adopt(true);
        // The map is a saved work now; the address no longer needs to carry it (F5 reopens the work).
        try { history.replaceState(null, '', location.pathname + location.search); } catch (e) { /* old browser */ }
    }
    stage('ready', { ms: Math.round(performance.now()), base: S.base, linked: linked ? linked.kind : '' });
    if (linked) ae('link_open', { kind: linked.kind, n: linked.kind === 'quiz' ? linked.task.refs.length : S.items.length });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
